import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createChart, ColorType } from 'lightweight-charts';
import { MaRibbonPaneView } from '../utils/chart/maRibbonSeries';
import {
  buildMaRibbonDataFromCandles,
  buildMaRibbonDataFromRows,
  MA_RIBBON_FAST_LEN,
  MA_RIBBON_SLOW_LEN,
} from '../utils/chart/maRibbonUtils';
import api from '../api';
import AuraWidget from '../components/aura/AuraWidget';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  buildCooldownNoticeFromSignal,
  buildPreservedActiveResult,
  getDbSignalTimestampMs,
  hydrateLatestSignalIdRefs,
  isNonExecutableSignalPayload,
  isRecentDbSavedSignal,
  isWithinSignalAnalysisWindow,
  shouldPreserveActiveSignalCard,
} from '../utils/niftySignalPageGuards';
import {
  formatMaRibbonPriceWithIndex,
  getMaRibbonTradeLevels,
  isMaRibbonSignal,
} from '../utils/niftyMaRibbonLevels';
import ClosingMomentumWindowPanel from '../components/nifty/ClosingMomentumWindowPanel';
import { isClosingMomentumPanelWindow } from '../utils/closingMomentumWindow';

/** Map persisted scheduler run (latest-analysis-run) to page result shape. */
function analysisRunToPageResult(run) {
  if (!run) return null;
  const status = run.status || 'no_active_signal';
  const payload = {
    status,
    message: run.message,
    strategy_progress: run.strategy_progress,
    live_indicators: run.live_indicators,
    ai_prioritization: run.ai_prioritization,
    market_narrative: run.market_narrative,
    dynamic_thresholds: run.dynamic_thresholds,
    current_price: run.spot_price,
    timestamp: run.timestamp,
    india_vix: run.india_vix,
    market_bias: run.market_bias,
    market_regime: run.market_regime,
  };
  if (status === 'active_signal' && run.signal_payload) {
    payload.signal = run.signal_payload;
  }
  return payload;
}

const NiftySignalAnalyzerPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  /** Set by TrendVector mobile WebView (`tv_embed=1`): hide site chrome for in-app viewing. */
  const isMobileEmbed = searchParams.get('tv_embed') === '1';
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(true); // Start with true to show loading on initial mount
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('5minute');
  const [autoRefresh, setAutoRefresh] = useState(true);
  // Indicator visibility state - all hidden by default, shown when user clicks indicator buttons
  // Indicators are calculated in background and ready to display instantly on toggle
  const [indicatorVisibility, setIndicatorVisibility] = useState({
    ema9: false,
    ema20: false,
    ema34: false,
    maRibbon: false,
    vwap: false,
    supertrend1: false,
    supertrend2: false,
    pivots: false,
    rsi14: false,
    fbrt: false,  // FBRT (Failed Breakout Re-Test) - only available on Daily timeframe
  });
  const indicatorVisibilityRef = useRef(indicatorVisibility);
  useEffect(() => {
    indicatorVisibilityRef.current = indicatorVisibility;
  }, [indicatorVisibility]);
  // Pattern visibility state (default: hidden)
  const [showPatterns, setShowPatterns] = useState(false);
  // Pattern filter state
  const [patternFilters, setPatternFilters] = useState({
    patternType: 'all', // 'all', 'Double Top', 'Double Bottom', 'Head & Shoulders', etc.
    status: 'all', // 'all', 'Forming', 'Confirmed'
    signal: 'all', // 'all', 'Bullish', 'Bearish'
    startDate: '',
    endDate: '',
  });
  const [lastUpdate, setLastUpdate] = useState(null);
  const [livePrice, setLivePrice] = useState(null);
  const [livePriceChange, setLivePriceChange] = useState(null);
  const [livePriceChangePercent, setLivePriceChangePercent] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [reconnectTrigger, setReconnectTrigger] = useState(0); // Trigger reconnection by incrementing
  const [reconnectAttempts, setReconnectAttempts] = useState(0); // Track reconnection attempts for UI
  const [updateCount, setUpdateCount] = useState(0); // Track number of real-time updates received
  const [currentTime, setCurrentTime] = useState(new Date()); // For continuous time display
  const [lastAnalysisTime, setLastAnalysisTime] = useState(null); // Track when analysis last ran
  const [analysisStatus, setAnalysisStatus] = useState(null); // Track analysis result status
  const [latestSignal, setLatestSignal] = useState(null); // Latest signal from database
  const lastActiveSignalResultRef = useRef(null); // Preserve full active-signal payload for UI (ref avoids poll loop)
  const [cooldownNotice, setCooldownNotice] = useState(null); // UI-only notice when a signal is blocked by cooldown/restrictions
  const [signalTimestamp, setSignalTimestamp] = useState(null); // Timestamp of latest signal
  const [closingMomentumWindow, setClosingMomentumWindow] = useState(null);
  const [closingMomentumLoading, setClosingMomentumLoading] = useState(false);
  const [closingMomentumError, setClosingMomentumError] = useState('');
  // Dynamic 1H support/resistance from backend SR engine
  const [dynamicSrLevels, setDynamicSrLevels] = useState(null);
  const [showDynamicSr, setShowDynamicSr] = useState(false);
  const updateCountRef = useRef(0); // Ref for logging purposes (to avoid dependency issues)
  const refreshIntervalRef = useRef(null);
  const timeIntervalRef = useRef(null); // For continuous time updates
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null); // Track reconnection timeout
  const reconnectAttemptsRef = useRef(0); // Track reconnection attempts for exponential backoff
  const lastMessageTimeRef = useRef(null); // Track last message time for connection health check
  const heartbeatIntervalRef = useRef(null); // Track heartbeat interval
  const isManualCloseRef = useRef(false); // Track if connection was manually closed
  const previousPriceRef = useRef(null); // To calculate price change
  const priceInitializedRef = useRef(false); // Track if price has been initialized from chart data
  const skipChartUpdateRef = useRef(false); // Skip full chart data update when only live candle is being updated
  const lastIndicatorValuesRef = useRef({ supertrend_1: null, supertrend_2: null }); // Store last indicator values for WebSocket to access
  const vwapCumulativePVRef = useRef(0); // Cumulative price*volume for VWAP
  const vwapCumulativeVolumeRef = useRef(0); // Cumulative volume for VWAP
  const rsiGainRef = useRef(0); // Smoothed gain for RSI
  const rsiLossRef = useRef(0); // Smoothed loss for RSI
  const atrRef = useRef(null); // Current ATR value for SuperTrend calculation
  const prevHighRef = useRef(null); // Previous high for TR calculation
  const prevLowRef = useRef(null); // Previous low for TR calculation
  const prevCloseRef = useRef(null); // Previous close for TR calculation
  const wsMessageQueueRef = useRef([]); // Queue for WebSocket messages until chart is ready
  const isChartReadyRef = useRef(false); // Track if chart series is initialized
  const chartLoadRequestIdRef = useRef(0); // Prevent stale chart responses from overwriting current timeframe
  const backgroundEnrichInFlightRef = useRef({ timeframe: null, active: false }); // Deduplicate enrichment fetches
  const lastKnownSignalIdRef = useRef(null); // Last signal_id we've seen; used to play sound only when a NEW signal is saved
  const hasHydratedLatestSignalRef = useRef(false); // Prevent sound on initial latest-signal hydration
  const latestSignalRef = useRef(null); // Synced from /latest-signal for analysis-run preserve logic
  const signalTimestampRef = useRef(null);
  const lastPlayedSignalIdRef = useRef(null); // Prevent duplicate sound for same signal id
  const lastSoundPlayedAtRef = useRef(0); // Debounce rapid duplicate trigger paths
  const activeSignalAudioRef = useRef(null); // Keep only one active audio instance

  useEffect(() => {
    latestSignalRef.current = latestSignal;
    signalTimestampRef.current = signalTimestamp;
  }, [latestSignal, signalTimestamp]);

  const signalHydrationRefs = useRef({
    hasHydrated: hasHydratedLatestSignalRef,
    lastKnownId: lastKnownSignalIdRef,
  }).current;

  /** Play alert when a new signal is generated and saved (only on this page). */
  const playNewSignalSound = useCallback((signalId = null) => {
    try {
      // 1) Strict dedup by signal id (same signal should alert only once)
      if (signalId && lastPlayedSignalIdRef.current === signalId) {
        return;
      }

      // 2) Short debounce across near-simultaneous sources (analyze + latest-signal)
      const now = Date.now();
      if (now - lastSoundPlayedAtRef.current < 1200) {
        return;
      }

      // 3) Prevent overlap by stopping any currently playing clip
      if (activeSignalAudioRef.current) {
        try {
          activeSignalAudioRef.current.pause();
          activeSignalAudioRef.current.currentTime = 0;
        } catch (_e) {
          // no-op
        }
      }

      const audio = new Audio('/sounds/new_trade_signal.mp3');
      audio.volume = 1;
      activeSignalAudioRef.current = audio;
      lastSoundPlayedAtRef.current = now;
      if (signalId) {
        lastPlayedSignalIdRef.current = signalId;
      }
      audio.play().catch((err) => console.warn('New signal sound play failed:', err));
    } catch (e) {
      console.warn('New signal sound failed:', e);
    }
  }, []);

  /** Sound only after initial /latest-signal hydration and only for a new signal_id. */
  const notifyIfNewSavedSignalId = useCallback(
    (signalId) => {
      const { isNew } = hydrateLatestSignalIdRefs(signalId, signalHydrationRefs);
      if (isNew) {
        playNewSignalSound(signalId);
      }
    },
    [playNewSignalSound, signalHydrationRefs]
  );

  const timeframes = [
    { value: '5minute', label: '5 Min' }, // 5 minutes
    { value: '15minute', label: '15 Min' }, // 15 minutes
    { value: '30minute', label: '30 Min' }, // 30 minutes
    { value: '60minute', label: '1 Hour' },
    { value: 'day', label: 'Daily' }, // Daily
    { value: 'week', label: 'Weekly' }
  ];

  /**
   * Fetch dynamic 1H support/resistance levels from backend SR engine.
   * 
   * We only fetch when the selected timeframe is one of:
   * - 5minute
   * - 15minute
   * - 30minute
   * - 60minute
   * 
   * Data is reused across toggles so user can show/hide instantly.
   */
  useEffect(() => {
    const tf = selectedTimeframe;
    const isSrTimeframe =
      tf === '5minute' || tf === '15minute' || tf === '30minute' || tf === '60minute';
    if (!isSrTimeframe) {
      // Clear cached levels when leaving intraday TFs that use SR
      setDynamicSrLevels(null);
      return;
    }

    let cancelled = false;

    const fetchSrLevels = async () => {
      try {
        console.log(`📡 Fetching dynamic SR levels for timeframe=${tf}`);
        const resp = await api.get('/api/v1/nifty/sr-levels?days=360');
        if (!cancelled) {
          setDynamicSrLevels(resp.data || null);
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || e.message || 'Failed to fetch support/resistance levels';
        console.error('❌ Error fetching SR levels:', msg);
        if (!cancelled) {
          setDynamicSrLevels(null);
        }
      }
    };

    fetchSrLevels();

    return () => {
      cancelled = true;
    };
  }, [selectedTimeframe]);

  /**
   * Fetch historical chart data via REST API.
   * This is used for:
   * - Initial data loading when page loads or timeframe changes
   * - Periodic full chart refreshes (less frequent, to get complete historical data with indicators)
   * 
   * WebSocket is used separately for real-time live price updates only.
   */
  // Infinite scroll state management
  const [historicalCandles, setHistoricalCandles] = useState([]); // Raw historical candle data
  const oldestTimestampRef = useRef(null); // Track the oldest data point we have loaded
  const isLoadingHistoryRef = useRef(false); // Prevent concurrent fetches
  const hasMoreHistoryRef = useRef(true); // Track if more historical data is available

  const hasAnyIndicatorData = useCallback((candles = []) => {
    if (!Array.isArray(candles) || candles.length === 0) return false;
    return candles.some((candle) => (
      candle?.ema_9 != null ||
      candle?.ema_20 != null ||
      candle?.ema_34 != null ||
      candle?.rsi_14 != null ||
      candle?.vwap != null ||
      candle?.supertrend_1 != null ||
      candle?.supertrend_2 != null
    ));
  }, []);

  const hasPivotLevelData = useCallback((payload) => {
    const piv = payload?.pivot_levels;
    if (!piv) return false;
    return Boolean(
      piv.pivot ||
      (Array.isArray(piv.support) && piv.support.length > 0) ||
      (Array.isArray(piv.resistance) && piv.resistance.length > 0)
    );
  }, []);

  const fetchBackgroundEnrichedChartData = useCallback(async (timeframe, requestId) => {
    if (!timeframe) return;
    if (
      backgroundEnrichInFlightRef.current.active &&
      backgroundEnrichInFlightRef.current.timeframe === timeframe
    ) {
      return;
    }

    backgroundEnrichInFlightRef.current = { timeframe, active: true };
    try {
      console.log(`🧠 Background indicator enrichment started for ${timeframe}`);
      const resp = await api.get(`/api/v1/nifty/chart-data?timeframe=${timeframe}&fast_mode=false`, {
        timeout: 60000,
      });
      const enrichedData = resp.data;
      const chartRows = enrichedData?.chart_data;
      if (!enrichedData || !Array.isArray(chartRows) || chartRows.length === 0) return;
      if (requestId !== chartLoadRequestIdRef.current) return; // stale timeframe/request

      skipChartUpdateRef.current = false;
      setHistoricalCandles(chartRows);
      setChartData(enrichedData);
      const sortedCandles = [...chartRows].sort((a, b) => a.time - b.time);
      const firstCandle = sortedCandles[0];
      if (firstCandle) {
        oldestTimestampRef.current = firstCandle.time;
        hasMoreHistoryRef.current = true;
      }
      setLastUpdate(new Date());
      console.log(`✅ Background enrichment complete for ${timeframe} (${chartRows.length} candles)`);
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Background enrichment failed';
      console.warn(`⚠️ Background indicator enrichment skipped for ${timeframe}:`, msg);
    } finally {
      if (requestId === chartLoadRequestIdRef.current) {
        backgroundEnrichInFlightRef.current = { timeframe: null, active: false };
      }
    }
  }, []);

  /**
   * Fetch initial chunk of historical data (most recent candles).
   * This loads fast and gets the chart displaying quickly.
   */
  const fetchInitialChartData = useCallback(async (timeframe = selectedTimeframe) => {
    const requestId = chartLoadRequestIdRef.current + 1;
    chartLoadRequestIdRef.current = requestId;
    setChartLoading(true);
    setError('');
    try {
      console.log(`📊 Fetching initial chart data for timeframe: ${timeframe}`);
      // Primary request: full metadata (indicators, patterns, etc.)
      const baseUrl = `/api/v1/nifty/chart-data?timeframe=${timeframe}`;
      let resp;
      try {
        resp = await api.get(baseUrl, {
          timeout: 60000 // 60 seconds for full chart data
        });
      } catch (primaryErr) {
        const timeoutLike =
          primaryErr?.code === 'ECONNABORTED' ||
          String(primaryErr?.message || '').toLowerCase().includes('timeout');
        if (!timeoutLike) {
          throw primaryErr;
        }
        // Fallback: retry once with backend fast path to keep chart usable during peak load.
        console.warn('⚠️ Chart request timed out; retrying once with fast_mode=true');
        resp = await api.get(`${baseUrl}&fast_mode=true`, {
          timeout: 30000
        });
      }
      const newData = resp.data;
      if (requestId !== chartLoadRequestIdRef.current) {
        return;
      }
      
      if (newData && newData.chart_data && newData.chart_data.length > 0) {
        // This is a full data refresh - ensure chart update is not skipped
        skipChartUpdateRef.current = false;
        
        // Store historical candles separately (for infinite scroll)
        setHistoricalCandles(newData.chart_data);
        
        // Store full chartData for metadata (indicators, patterns, etc.)
        setChartData(newData);
        
        // Track the oldest timestamp (first candle is the oldest)
        const sortedCandles = [...newData.chart_data].sort((a, b) => a.time - b.time);
        const firstCandle = sortedCandles[0];
        oldestTimestampRef.current = firstCandle.time;
        hasMoreHistoryRef.current = true;
        
        console.log(`✅ Initial data loaded: ${newData.chart_data.length} candles. Oldest: ${new Date(firstCandle.time * 1000).toISOString()}`);

        // Keep initial chart fast, then enrich indicators in background for instant toggles.
        const needsBackgroundEnrichment = Boolean(newData.fast_path) || !hasAnyIndicatorData(newData.chart_data);
        if (needsBackgroundEnrichment) {
          void fetchBackgroundEnrichedChartData(timeframe, requestId);
        }
      }
      
      setLastUpdate(new Date());
    } catch (e) {
      if (requestId !== chartLoadRequestIdRef.current) {
        return;
      }
      const msg = e?.response?.data?.detail || e.message || 'Failed to fetch chart data';
      setError(msg);
      console.error('❌ Error fetching initial chart data:', msg);
    } finally {
      if (requestId === chartLoadRequestIdRef.current) {
        setChartLoading(false);
      }
    }
  }, [selectedTimeframe, fetchBackgroundEnrichedChartData, hasAnyIndicatorData]);

  /**
   * Fetch older historical data chunk for infinite scroll.
   * This is called when user scrolls left to view older data.
   */
  const fetchOlderHistory = useCallback(async (timeframe = selectedTimeframe, beforeTimestamp) => {
    if (isLoadingHistoryRef.current || !hasMoreHistoryRef.current || !beforeTimestamp) {
      return;
    }

    isLoadingHistoryRef.current = true;
    try {
      console.log(`📊 Fetching older data before ${new Date(beforeTimestamp * 1000).toISOString()}`);
      
      // Use new chunked endpoint - returns candles with indicators
      const resp = await api.get(`/api/v1/nifty/chart-data?timeframe=${timeframe}&before=${beforeTimestamp}&limit=300`);
      const olderCandles = Array.isArray(resp.data) ? resp.data : [];
      
      if (olderCandles.length > 0) {
        // Remove duplicates and prepend older candles
        setHistoricalCandles(prevCandles => {
          const existingTimes = new Set(prevCandles.map(c => c.time));
          const newCandles = olderCandles.filter(c => !existingTimes.has(c.time));
          
          if (newCandles.length > 0) {
            // Prepend and sort by time
            const merged = [...newCandles, ...prevCandles].sort((a, b) => a.time - b.time);
            
            // Update oldest timestamp
            oldestTimestampRef.current = merged[0].time;
            
            console.log(`✅ Prepend ${newCandles.length} older candles. Total: ${merged.length} candles`);
            
            // Update chart series immediately
            if (candlestickSeriesRef.current) {
              const candleData = merged.map(d => ({
                time: d.time,
                open: d.open,
                high: d.high,
                low: d.low,
                close: d.close,
              }));
              candlestickSeriesRef.current.setData(candleData);
              
              // Update volume series
              if (volumeSeriesRef.current) {
                const volumeData = merged.map(d => ({
                  time: d.time,
                  value: d.volume || 0,
                  color: (d.close >= d.open) ? 'rgba(0, 150, 0, 0.5)' : 'rgba(255, 0, 0, 0.5)',
                }));
                volumeSeriesRef.current.setData(volumeData);
              }
              
              // === UPDATE INDICATOR SERIES WITH OLDER DATA ===
              // The backend returns indicators for chunked data, so we update all indicator series
              
              // Update EMA 9 series
              if (ema9SeriesRef.current) {
                try {
                  const ema9Data = merged
                    .filter(d => d.ema_9 !== null && d.ema_9 !== undefined && !isNaN(d.ema_9))
                    .map(d => ({ time: d.time, value: parseFloat(d.ema_9) }));
                  if (ema9Data.length > 0) {
                    ema9SeriesRef.current.setData(ema9Data);
                  }
                } catch (error) {
                  console.error('Error updating EMA 9 with older data:', error);
                }
              }

              if (maRibbonSeriesRef.current) {
                try {
                  const ribbonData = buildMaRibbonDataFromRows(merged);
                  if (ribbonData.length > 0) {
                    maRibbonSeriesRef.current.setData(ribbonData);
                    const lastRibbon = ribbonData[ribbonData.length - 1];
                    maRibbonFastEmaRef.current = lastRibbon.fast;
                    maRibbonSlowEmaRef.current = lastRibbon.slow;
                  }
                } catch (error) {
                  console.error('Error updating MA Ribbon with older data:', error);
                }
              }
              
              // Update EMA 20 series
              if (ema20SeriesRef.current) {
                try {
                  const ema20Data = merged
                    .filter(d => d.ema_20 !== null && d.ema_20 !== undefined && !isNaN(d.ema_20))
                    .map(d => ({ time: d.time, value: parseFloat(d.ema_20) }));
                  if (ema20Data.length > 0) {
                    ema20SeriesRef.current.setData(ema20Data);
                  }
                } catch (error) {
                  console.error('Error updating EMA 20 with older data:', error);
                }
              }
              
              // Update EMA 34 series
              if (ema34SeriesRef.current) {
                try {
                  const ema34Data = merged
                    .filter(d => d.ema_34 !== null && d.ema_34 !== undefined && !isNaN(d.ema_34))
                    .map(d => ({ time: d.time, value: parseFloat(d.ema_34) }));
                  if (ema34Data.length > 0) {
                    ema34SeriesRef.current.setData(ema34Data);
                  }
                } catch (error) {
                  console.error('Error updating EMA 34 with older data:', error);
                }
              }
              
              // Update VWAP series
              if (vwapSeriesRef.current) {
                try {
                  const vwapData = merged
                    .filter(d => d.vwap !== null && d.vwap !== undefined && !isNaN(d.vwap))
                    .map(d => ({ time: d.time, value: parseFloat(d.vwap) }));
                  if (vwapData.length > 0) {
                    vwapSeriesRef.current.setData(vwapData);
                  }
                } catch (error) {
                  console.error('Error updating VWAP with older data:', error);
                }
              }
              
              // Update RSI 14 series
              if (rsiSeriesRef.current) {
                try {
                  const rsiData = merged
                    .filter(d => d.rsi_14 !== null && d.rsi_14 !== undefined && !isNaN(d.rsi_14))
                    .map(d => ({ time: d.time, value: parseFloat(d.rsi_14) }));
                  if (rsiData.length > 0) {
                    rsiSeriesRef.current.setData(rsiData);
                  }
                } catch (error) {
                  console.error('Error updating RSI with older data:', error);
                }
              }
              
              // Update SuperTrend series (rebuild segments with merged data)
              // SuperTrend uses segmented line series, so we need to update each segment's data
              if (supertrend1SeriesRef.current && Array.isArray(supertrend1SeriesRef.current)) {
                try {
                  // Prepare SuperTrend 1 data from merged candles
                  const st1Data = merged
                    .filter(d => d.supertrend_1 !== null && d.supertrend_1 !== undefined && !isNaN(d.supertrend_1))
                    .map(d => ({ 
                      time: d.time, 
                      value: parseFloat(d.supertrend_1),
                      direction: d.supertrend_1_direction
                    }));
                  
                  if (st1Data.length > 0) {
                    // For simplicity, update all existing series with the full data
                    // The segments will display based on their original color (green/red)
                    // Note: This is a simplified approach - full segment rebuilding would require
                    // removing old series and creating new ones with proper segmentation
                    const allSt1Points = st1Data.map(d => ({ time: d.time, value: d.value }));
                    supertrend1SeriesRef.current.forEach(series => {
                      try {
                        series.setData(allSt1Points);
                      } catch (e) {
                        // Individual series update failed - might be removed
                      }
                    });
                  }
                } catch (error) {
                  console.error('Error updating SuperTrend 1 with older data:', error);
                }
              }
              
              if (supertrend2SeriesRef.current && Array.isArray(supertrend2SeriesRef.current)) {
                try {
                  // Prepare SuperTrend 2 data from merged candles
                  const st2Data = merged
                    .filter(d => d.supertrend_2 !== null && d.supertrend_2 !== undefined && !isNaN(d.supertrend_2))
                    .map(d => ({ 
                      time: d.time, 
                      value: parseFloat(d.supertrend_2),
                      direction: d.supertrend_2_direction
                    }));
                  
                  if (st2Data.length > 0) {
                    const allSt2Points = st2Data.map(d => ({ time: d.time, value: d.value }));
                    supertrend2SeriesRef.current.forEach(series => {
                      try {
                        series.setData(allSt2Points);
                      } catch (e) {
                        // Individual series update failed - might be removed
                      }
                    });
                  }
                } catch (error) {
                  console.error('Error updating SuperTrend 2 with older data:', error);
                }
              }
              
              console.log('✅ Updated all indicator series with older data');
            }
            
            return merged;
          }
          return prevCandles;
        });
      } else {
        // No more data available
        hasMoreHistoryRef.current = false;
        console.log('📊 No more historical data available');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Failed to fetch older data';
      console.error('❌ Error fetching older history:', msg);
      // Don't set hasMoreHistoryRef to false on error - might be temporary
    } finally {
      isLoadingHistoryRef.current = false;
    }
  }, [selectedTimeframe]);

  /**
   * Legacy fetchChartData - kept for backward compatibility with existing code
   * that might call it for full refreshes with indicators/patterns
   */
  const fetchChartData = useCallback(async (timeframe = selectedTimeframe, updateOnly = false) => {
    // For now, just call fetchInitialChartData
    // This maintains compatibility with existing refresh logic
    if (!updateOnly) {
      await fetchInitialChartData(timeframe);
    }
  }, [selectedTimeframe, fetchInitialChartData]);

  /**
   * Apply scheduler/analysis-run payload to Signal Analysis UI (no run_analysis).
   */
  const applyAnalysisPayloadToUI = useCallback(
    (data) => {
      if (!data) return;

      console.log('📊 Latest analysis run mapped:', data.status);

      setLastAnalysisTime(data.timestamp ? new Date(data.timestamp) : new Date());

      const dbSignal = latestSignalRef.current;
      const dbTimestamp = signalTimestampRef.current;

      if (shouldPreserveActiveSignalCard(data.status, dbSignal, dbTimestamp)) {
        setAnalysisStatus('active_signal');
        setCooldownNotice(null);
        const preserved = buildPreservedActiveResult(data, dbSignal);
        setResult((prev) => {
          if (prev?.status === 'active_signal' && prev?.signal?.signal_id === dbSignal.signal_id) {
            return {
              ...preserved,
              signal: { ...prev.signal, ...dbSignal },
            };
          }
          const lastActive = lastActiveSignalResultRef.current;
          if (lastActive?.signal) {
            return {
              ...preserved,
              signal: { ...lastActive.signal, ...dbSignal },
            };
          }
          return preserved;
        });
        lastActiveSignalResultRef.current = {
          ...preserved,
          signal: dbSignal,
        };
        return;
      }

      setAnalysisStatus(data.status);

      if (data.status === 'active_signal' && data.signal) {
        if (isNonExecutableSignalPayload(data.signal)) {
          setCooldownNotice(buildCooldownNoticeFromSignal(data.signal, data.timestamp));
          setAnalysisStatus('no_active_signal');
          setResult((prevResult) => {
            if (prevResult?.status === 'active_signal' && prevResult.signal) {
              return prevResult;
            }
            if (isRecentDbSavedSignal(dbSignal, dbTimestamp)) {
              return buildPreservedActiveResult(data, dbSignal);
            }
            const blockedMessage = data.signal?.save_failed
              ? 'Signal could not be saved to database. Showing last saved signal.'
              : 'Signal blocked by cooldown/restrictions.';
            return {
              status: 'no_active_signal',
              message: blockedMessage,
              strategy_progress: data.strategy_progress,
              live_indicators: data.live_indicators,
            };
          });
          return;
        }

        setCooldownNotice(null);
        setResult((prevResult) => {
          const newSignal = data.signal;
          const newId = newSignal?.signal_id;

          if (prevResult?.signal && newId && prevResult.signal.signal_id === newId) {
            return {
              ...prevResult,
              ...data,
              status: 'active_signal',
              signal: { ...prevResult.signal, ...newSignal },
            };
          }

          return {
            ...data,
            status: 'active_signal',
            signal: newSignal,
          };
        });

        lastActiveSignalResultRef.current = {
          ...data,
          status: 'active_signal',
          signal: data.signal,
        };
      } else if (data.status === 'no_active_signal' || data.status === 'no_signal') {
        setResult(data);
      } else if (data.status === 'data_error') {
        setError(data.message || 'Data error occurred during analysis');
        setResult(data);
      } else if (data.status === 'analysis_error') {
        setError(`Analysis Error: ${data.message || 'Unknown backend error'}`);
        setResult(data);
      } else {
        setResult(data);
      }
    },
    []
  );

  /** Read latest scheduler snapshot from DB (strategy progress, no run_analysis). */
  const fetchLatestAnalysisRun = useCallback(async () => {
    try {
      const resp = await api.get('/api/v1/nifty/latest-analysis-run', { timeout: 15000 });
      const run = resp.data?.run;
      if (!run) {
        console.log('📋 No analysis runs in DB yet');
        return;
      }
      applyAnalysisPayloadToUI(analysisRunToPageResult(run));
    } catch (e) {
      console.error('❌ Error fetching latest analysis run:', e);
    }
  }, [applyAnalysisPayloadToUI]);

  const fetchLatestAnalysisRunRef = useRef(fetchLatestAnalysisRun);
  useEffect(() => {
    fetchLatestAnalysisRunRef.current = fetchLatestAnalysisRun;
  }, [fetchLatestAnalysisRun]);

  const fetchClosingMomentumWindow = useCallback(async () => {
    if (!isClosingMomentumPanelWindow()) {
      setClosingMomentumWindow(null);
      setClosingMomentumError('');
      return;
    }
    setClosingMomentumLoading(true);
    try {
      const resp = await api.get('/api/v1/nifty/closing-momentum-window', { timeout: 20000 });
      setClosingMomentumWindow(resp.data || null);
      setClosingMomentumError('');
    } catch (e) {
      console.error('❌ Error fetching closing momentum window:', e);
      setClosingMomentumError('Unable to load 3 PM window data');
    } finally {
      setClosingMomentumLoading(false);
    }
  }, []);

  const fetchClosingMomentumWindowRef = useRef(fetchClosingMomentumWindow);
  useEffect(() => {
    fetchClosingMomentumWindowRef.current = fetchClosingMomentumWindow;
  }, [fetchClosingMomentumWindow]);

  useEffect(() => {
    const tick = () => {
      if (isClosingMomentumPanelWindow()) {
        fetchClosingMomentumWindowRef.current();
      } else {
        setClosingMomentumWindow(null);
        setClosingMomentumError('');
      }
    };

    tick();
    const intervalId = setInterval(tick, 30000);
    return () => clearInterval(intervalId);
  }, []);

  /**
   * Fetch latest signal from database on page load.
   * This shows the most recent signal that was generated by the scheduled analyzer.
   *
   * IMPORTANT:
   * - NEVER clear the "Latest Signal" section. Always show the last generated signal.
   * - Only update when a new signal is available from the backend.
   * - Preserve existing signal state on errors or when backend has no new signal.
   */
  const fetchLatestSignal = useCallback(async () => {
    try {
      const resp = await api.get('/api/v1/nifty/latest-signal');
      console.log('📡 Latest signal API response:', resp.data);
      
      if (resp.data && resp.data.signal) {
        const incoming = resp.data.signal;
        const newId = incoming.signal_id;

        notifyIfNewSavedSignalId(newId);

        // Latest Signal card only — never expires; Signal Analysis comes from /latest-analysis-run.
        setLatestSignal(incoming);
        setSignalTimestamp(resp.data.timestamp);

        console.log('✅ Latest signal updated:', incoming.signal_type, incoming.strategy_type);
      } else {
        // Backend has no signal, but DO NOT clear the existing signal
        // Keep showing the last generated signal
        console.log('📋 No new signal from backend, preserving existing Latest Signal');
        // Do nothing - keep existing latestSignal state
      }
    } catch (e) {
      console.error('❌ Error fetching latest signal:', e);
      // Don't clear existing signal on error - preserve it
      // The signal will be updated on next successful fetch
    }
  }, [notifyIfNewSavedSignalId]);

  const fetchLatestSignalRef = useRef(fetchLatestSignal);
  useEffect(() => {
    fetchLatestSignalRef.current = fetchLatestSignal;
  }, [fetchLatestSignal]);

  /** Manual refresh: latest signal + latest scheduler run (read-only). */
  const refreshSignalStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await fetchLatestSignal();
      await fetchLatestAnalysisRun();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Failed to refresh status';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [fetchLatestSignal, fetchLatestAnalysisRun]);

  /**
   * After chart REST payload is ready, refresh latest-signal (read-only; no run_analysis).
   */
  useEffect(() => {
    if (!chartData?.chart_data?.length) return;
    fetchLatestSignal();
  }, [chartData, fetchLatestSignal]);

  /**
   * Continuous time update timer.
   * Updates the current time display every second for live indication.
   */
  useEffect(() => {
    // Update time immediately
    setCurrentTime(new Date());
    
    // Set up interval to update time every second
    timeIntervalRef.current = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
        timeIntervalRef.current = null;
      }
    };
  }, []);

  /**
   * On load: poll latest signal + latest scheduler analysis run (read-only).
   * Uses refs so callback identity changes do not restart the interval.
   */
  useEffect(() => {
    const loadStatus = async () => {
      await fetchLatestSignalRef.current();
      await fetchLatestAnalysisRunRef.current();
    };
    loadStatus();

    const pollInterval = setInterval(loadStatus, 60000);

    return () => clearInterval(pollInterval);
  }, []);

  /**
   * Signal Analysis card expires after 30m (DB save time). Latest Signal never expires.
   * After expiry, refresh scheduler run for strategy progress only.
   */
  useEffect(() => {
    const checkSignalExpiry = () => {
      const dbSignal = latestSignalRef.current;
      const dbTsMs = getDbSignalTimestampMs(dbSignal, signalTimestampRef.current);
      if (!dbSignal || Number.isNaN(dbTsMs)) {
        return;
      }

      if (!isWithinSignalAnalysisWindow(dbTsMs)) {
        console.log('⏰ Signal Analysis window ended (>30 min from DB save), refreshing strategy progress...');
        fetchLatestAnalysisRunRef.current?.();
      }
    };

    // Check immediately
    checkSignalExpiry();

    // Set up interval to check for signal expiry every minute
    const expiryCheckInterval = setInterval(checkSignalExpiry, 60000);

    return () => {
      clearInterval(expiryCheckInterval);
    };
  }, []);

  /**
   * Initial data loading via REST API.
   * This effect runs:
   * - On component mount (initial load)
   * - When timeframe changes
   * 
   * This fetches the initial chunk of historical data quickly.
   */
  useEffect(() => {
    // Reset state when timeframe changes
    lastLiveCandleRef.current = null;
    setLivePrice(null);
    setLivePriceChange(null);
    setLivePriceChangePercent(null);
    previousPriceRef.current = null;
    setUpdateCount(0);
    setHistoricalCandles([]);
    oldestTimestampRef.current = null;
    hasMoreHistoryRef.current = true;
    isLoadingHistoryRef.current = false;
    
    // Fetch initial data
    fetchInitialChartData(selectedTimeframe);
  }, [selectedTimeframe, fetchInitialChartData]);

  /**
   * WebSocket connection for live data streaming - ISOLATED EFFECT.
   * 
   * This effect is completely separate from REST API calls and chart data:
   * - REST API: Loads complete historical data with indicators (on mount, timeframe change, periodic refresh)
   * - WebSocket: Streams only live price updates for the current forming candle
   * 
   * Key design principles:
   * - NO dependency on chartData to avoid stale closures
   * - Uses functional setState to always work with fresh state
   * - Only depends on autoRefresh and selectedTimeframe
   */
  useEffect(() => {
    if (!autoRefresh) {
      // Close WebSocket if auto-refresh is disabled
      isManualCloseRef.current = true; // Mark as manual close
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      // Clear reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // Clear heartbeat interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      setWsConnected(false);
      reconnectAttemptsRef.current = 0; // Reset attempts
      setReconnectAttempts(0); // Reset UI counter
      return;
    }
    
    // Reset manual close flag when auto-refresh is enabled
    isManualCloseRef.current = false;
    
    // Wait a brief moment for initial chart data to load before connecting WebSocket
    // This prevents race conditions but doesn't create a dependency
    const connectionDelay = setTimeout(() => {
      console.log('🔌 Initializing WebSocket connection...');

      // Get WebSocket URL from API base URL
      const getWebSocketUrl = () => {
        // Use the same base URL as the API client for consistency
        const apiUrl = api.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
        console.log('🔌 API Base URL:', apiUrl);
        
        // Convert http/https to ws/wss
        let wsUrl = apiUrl.replace(/^http/, 'ws');
        // Ensure we use wss for https URLs
        if (apiUrl.startsWith('https')) {
          wsUrl = apiUrl.replace(/^https/, 'wss');
        }
        
        // Get auth token for WebSocket connection
        const token = localStorage.getItem('token');
        // WebSocket endpoint with token and timeframe in query parameters
        const params = new URLSearchParams();
        if (token) params.append('token', token);
        params.append('timeframe', selectedTimeframe);
        const fullWsUrl = `${wsUrl}/api/v1/nifty/ws/live-data?${params.toString()}`;
        console.log('🔌 Constructed WebSocket URL:', fullWsUrl.replace(/token=[^&]*/, 'token=***'));
        return fullWsUrl;
      };

      const wsUrl = getWebSocketUrl();
      console.log('🔌 Connecting to WebSocket:', wsUrl.replace(/token=[^&]*/, 'token=***'));

      // Create WebSocket connection
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ WebSocket connected for NIFTY50 live data');
        console.log('📡 WebSocket URL:', wsUrl.replace(/token=[^&]*/, 'token=***'));
        console.log('📊 Timeframe:', selectedTimeframe);
        setWsConnected(true);
        reconnectAttemptsRef.current = 0; // Reset reconnection attempts on successful connection
        setReconnectAttempts(0); // Reset UI counter
        lastMessageTimeRef.current = Date.now(); // Initialize last message time
        
        // Start heartbeat/health check to detect stale connections
        // Check every 30 seconds if we're still receiving messages
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
        heartbeatIntervalRef.current = setInterval(() => {
          const now = Date.now();
          const timeSinceLastMessage = lastMessageTimeRef.current 
            ? now - lastMessageTimeRef.current 
            : Infinity;
          
          // If no message received in 60 seconds, connection might be stale
          if (timeSinceLastMessage > 60000 && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            console.warn('⚠️ No WebSocket messages received in 60 seconds. Connection might be stale. Reconnecting...');
            wsRef.current.close();
            wsRef.current = null;
            setWsConnected(false);
            // Trigger reconnection
            attemptReconnect();
          }
        }, 30000); // Check every 30 seconds
      };

      ws.onmessage = (event) => {
      try {
        // Update last message time for connection health check
        lastMessageTimeRef.current = Date.now();
        
        const message = JSON.parse(event.data);
        
        // Log all incoming messages for debugging (throttled to avoid spam)
        const messageType = message.type || 'unknown';
        if (messageType === 'live_update' || messageType === 'live_price') {
          // Only log every 10th message to avoid console spam
          updateCountRef.current += 1;
          if (updateCountRef.current % 10 === 0 || updateCountRef.current <= 5) {
            console.log(`📨 WebSocket message received: type=${messageType}, price=${message.price || message.close}, timestamp=${message.timestamp}`);
          }
        } else {
          console.log(`📨 WebSocket message received: type=${messageType}`, message);
        }
        
        // Log connection status on first few messages to verify connection is working
        if (updateCountRef.current <= 3) {
          console.log(`✅ WebSocket is receiving messages! Connection status: ${ws.readyState === WebSocket.OPEN ? 'OPEN' : ws.readyState}`);
        }
        
        // Helper function to sanitize and prepare candle data for chart update
        const prepareCandleData = (msg) => {
          // Backend sends timestamp in milliseconds, convert to seconds for chart
          const candleTime = msg.timestamp ? msg.timestamp / 1000 : (msg.time ? (typeof msg.time === 'number' ? msg.time : new Date(msg.time).getTime() / 1000) : 0);
          
          // Sanitize OHLC values to ensure they are valid numbers
          const sanitizeValue = (val) => {
            const num = Number(val);
            return isNaN(num) || num <= 0 ? null : num;
          };
          
          const candleData = {
            time: candleTime,
            open: sanitizeValue(msg.open),
            high: sanitizeValue(msg.high),
            low: sanitizeValue(msg.low),
            close: sanitizeValue(msg.close),
          };
          
          // Validate all required fields are present and valid
          if (!candleData.time || !candleData.open || !candleData.high || !candleData.low || !candleData.close) {
            return null;
          }
          
          return candleData;
        };
        
        // Helper function to update volume series if available
        const updateVolume = (candleTime, close, open, volume) => {
          if (volumeSeriesRef.current && volume !== undefined && volume !== null) {
            try {
              const volumeData = {
                time: candleTime,
                value: Number(volume) || 0,
                color: close >= open 
                  ? 'rgba(0, 150, 0, 0.5)' 
                  : 'rgba(255, 0, 0, 0.5)',
              };
              volumeSeriesRef.current.update(volumeData);
            } catch (volError) {
              console.error('❌ Error updating volume:', volError);
            }
          }
        };
        
        // Helper functions for incremental indicator calculations (accessible to all message handlers)
        const calculateLiveEMA = (prevEMA, currentPrice, period) => {
          if (prevEMA === null || prevEMA === undefined || isNaN(prevEMA)) return prevEMA;
          const multiplier = 2 / (period + 1);
          return (currentPrice - prevEMA) * multiplier + prevEMA;
        };
        
        const calculateLiveVWAP = (currentClose, currentVolume) => {
          if (!currentVolume || currentVolume <= 0) return vwapCumulativePVRef.current / vwapCumulativeVolumeRef.current;
          
          // Update cumulative values
          vwapCumulativePVRef.current += currentClose * currentVolume;
          vwapCumulativeVolumeRef.current += currentVolume;
          
          // Calculate VWAP
          if (vwapCumulativeVolumeRef.current > 0) {
            return vwapCumulativePVRef.current / vwapCumulativeVolumeRef.current;
          }
          return null;
        };
        
        const calculateLiveRSI = (currentClose, prevClose, period = 14) => {
          if (!prevClose || prevClose === currentClose) return null;
          
          // Calculate price change
          const change = currentClose - prevClose;
          const gain = change > 0 ? change : 0;
          const loss = change < 0 ? -change : 0;
          
          // Update smoothed gain/loss using Wilder's smoothing
          const alpha = 1 / period;
          rsiGainRef.current = (rsiGainRef.current * (1 - alpha)) + (gain * alpha);
          rsiLossRef.current = (rsiLossRef.current * (1 - alpha)) + (loss * alpha);
          
          // Calculate RSI
          if (rsiLossRef.current === 0) return 100;
          const rs = rsiGainRef.current / rsiLossRef.current;
          return 100 - (100 / (1 + rs));
        };
        
        const calculateLiveSuperTrend = (high, low, close, prevSuperTrend, length = 10, factor = 1.5) => {
          if (atrRef.current === null || !prevHighRef.current || !prevLowRef.current || !prevCloseRef.current) {
            return prevSuperTrend; // Return previous value if refs not initialized
          }
          
          // Calculate True Range for current candle
          const tr1 = high - low;
          const tr2 = Math.abs(high - prevCloseRef.current);
          const tr3 = Math.abs(low - prevCloseRef.current);
          const trueRange = Math.max(tr1, tr2, tr3);
          
          // Update ATR incrementally using Wilder's smoothing
          const alpha = 1 / length;
          atrRef.current = (atrRef.current * (1 - alpha)) + (trueRange * alpha);
          
          // Calculate basic bands
          const hl2 = (high + low) / 2;
          const basicUpperBand = hl2 + (factor * atrRef.current);
          const basicLowerBand = hl2 - (factor * atrRef.current);
          
          // Simplified SuperTrend logic (close approximation)
          // Full SuperTrend requires tracking final bands, but this gives good approximation
          let supertrend;
          if (close > prevSuperTrend) {
            supertrend = basicLowerBand;
          } else {
            supertrend = basicUpperBand;
          }
          
          // Update refs for next calculation
          prevHighRef.current = high;
          prevLowRef.current = low;
          prevCloseRef.current = close;
          
          return supertrend;
        };
        
        // Use switch statement for cleaner message type handling
        switch (message.type) {
          case 'connected':
            console.log('✅ WebSocket connection confirmed:', message.message);
            console.log(`📊 Timeframe: ${message.timeframe}, Interval: ${message.interval_minutes} minutes`);
            break;
            
          case 'ws_ready':
            console.log('✅ WebSocket ready and listening for ticks:', message.message);
            console.log(`📊 Instrument token: ${message.instrument_token}`);
            break;
            
          case 'live_update':
            // The currently forming candle is being updated in real-time
            // CRITICAL: For live updates, we must use the SAME timestamp as the last candle
            // to ensure update() modifies the existing candle instead of creating a new one
            setUpdateCount(prev => prev + 1);
            
            // If chart is not ready, queue the message for later processing
            if (!candlestickSeriesRef.current || !isChartReadyRef.current) {
              console.log('⏳ Chart series not yet initialized. Queuing live_update message.');
              wsMessageQueueRef.current.push(message);
              // Limit queue size to prevent memory issues
              if (wsMessageQueueRef.current.length > 100) {
                wsMessageQueueRef.current.shift(); // Remove oldest message
              }
              return;
            }
            
            try {
              // Prepare candle data from message
              const rawCandleData = prepareCandleData(message);
              
              if (!rawCandleData) {
                console.warn('⚠️ Invalid candle data in live_update, skipping:', message);
                return;
              }
              
              // CRITICAL: For live updates, we need to update the CURRENT forming candle
              // If lastLiveCandleRef exists and has the same timestamp, use it (same interval)
              // If timestamp is different, it's a new interval - use the message timestamp
              let candleData = { ...rawCandleData };
              const isNewInterval = !lastLiveCandleRef.current || lastLiveCandleRef.current.time !== candleData.time;
              
              // Check if we're updating the same candle interval
              if (lastLiveCandleRef.current && lastLiveCandleRef.current.time === candleData.time) {
                // Same interval - keep using the same timestamp to update the existing candle
                console.debug(`🔄 Updating live candle at time ${candleData.time}`);
              } else {
                // Different timestamp - this is a new candle interval
                // Use the timestamp from the message (don't override it)
                // This will create/update a candle with the new timestamp
                if (lastLiveCandleRef.current) {
                  console.log(`🆕 New candle interval started: ${new Date(candleData.time * 1000).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })} (previous: ${new Date(lastLiveCandleRef.current.time * 1000).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })})`);
                } else {
                  console.log(`🆕 First live candle at time ${new Date(candleData.time * 1000).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}`);
                }
              }
              
              // Update the current forming candle (updates the last bar in real-time)
              // The update() method will update the candle with matching timestamp, or create a new one if timestamp differs
              candlestickSeriesRef.current.update(candleData);
              
              // Store reference for future updates (preserve the timestamp)
              lastLiveCandleRef.current = { ...candleData };

              // MA Ribbon: update on every tick (EMA 9 / 21 cloud aligned with strategy)
              if (
                maRibbonSeriesRef.current &&
                indicatorVisibilityRef.current.maRibbon &&
                maRibbonFastEmaRef.current != null &&
                maRibbonSlowEmaRef.current != null
              ) {
                const liveFast = calculateLiveEMA(
                  maRibbonFastEmaRef.current,
                  candleData.close,
                  MA_RIBBON_FAST_LEN
                );
                const liveSlow = calculateLiveEMA(
                  maRibbonSlowEmaRef.current,
                  candleData.close,
                  MA_RIBBON_SLOW_LEN
                );
                try {
                  maRibbonSeriesRef.current.update({
                    time: candleData.time,
                    fast: liveFast,
                    slow: liveSlow,
                  });
                } catch (e) {
                  // Series may not be ready yet
                }
              }
              
              // IMPORTANT: Also update historicalCandles state to keep it in sync
              // This ensures that when the chart re-renders, it has the latest live candle
              // Set flag to skip full chart update (we already updated via candlestickSeriesRef)
              skipChartUpdateRef.current = true;
              setHistoricalCandles(prevCandles => {
                if (prevCandles.length === 0) return prevCandles;
                
                // Get last completed candle for indicator baseline
                const lastCompletedCandle = prevCandles[prevCandles.length - 1];
                
                // OPTIMIZATION: Only recalculate indicators when a new candle interval starts
                // For intra-candle updates (same timestamp), use previous indicator values
                let liveEMA9, liveEMA20, liveEMA34, liveVWAP, liveRSI, liveST1, liveST2;
                
                if (isNewInterval) {
                  // NEW INTERVAL: Recalculate all indicators
                  console.debug('📊 New interval detected - recalculating indicators');
                  
                  liveEMA9 = calculateLiveEMA(lastCompletedCandle?.ema_9, candleData.close, 9);
                  liveEMA20 = calculateLiveEMA(lastCompletedCandle?.ema_20, candleData.close, 20);
                  liveEMA34 = calculateLiveEMA(lastCompletedCandle?.ema_34, candleData.close, 34);
                  liveVWAP = calculateLiveVWAP(candleData.close, message.volume || 0);
                  
                  const prevClose = lastCompletedCandle?.close;
                  liveRSI = prevClose ? calculateLiveRSI(candleData.close, prevClose) : lastCompletedCandle?.rsi_14;
                  
                  liveST1 = calculateLiveSuperTrend(
                    candleData.high, 
                    candleData.low, 
                    candleData.close, 
                    lastCompletedCandle?.supertrend_1 ?? null,
                    10,
                    1.5
                  );
                  
                  liveST2 = calculateLiveSuperTrend(
                    candleData.high,
                    candleData.low,
                    candleData.close,
                    lastCompletedCandle?.supertrend_2 ?? null,
                    10,
                    2.0
                  );
                  
                  // Update indicator refs for next calculation
                  lastIndicatorValuesRef.current = {
                    supertrend_1: liveST1,
                    supertrend_2: liveST2,
                  };
                } else {
                  // SAME INTERVAL: Keep previous indicator values (no recalculation)
                  // This reduces CPU usage - indicators only update when candle completes
                  liveEMA9 = lastCompletedCandle?.ema_9 ?? null;
                  liveEMA20 = lastCompletedCandle?.ema_20 ?? null;
                  liveEMA34 = lastCompletedCandle?.ema_34 ?? null;
                  liveVWAP = lastCompletedCandle?.vwap ?? null;
                  liveRSI = lastCompletedCandle?.rsi_14 ?? null;
                  liveST1 = lastCompletedCandle?.supertrend_1 ?? null;
                  liveST2 = lastCompletedCandle?.supertrend_2 ?? null;
                }
                
                // Update indicator series on chart ONLY when new interval starts
                // This limits chart updates to once per candle (every 5 minutes for 5min chart)
                if (isNewInterval) {
                  // Update EMA series on chart for visual update
                  if (liveEMA9 !== null && liveEMA9 !== undefined && ema9SeriesRef.current && indicatorVisibility.ema9) {
                  try {
                    ema9SeriesRef.current.update({ time: candleData.time, value: liveEMA9 });
                  } catch (e) {
                    // Silently handle - series might not be initialized yet
                  }
                }
                
                if (liveEMA20 !== null && liveEMA20 !== undefined && ema20SeriesRef.current && indicatorVisibility.ema20) {
                  try {
                    ema20SeriesRef.current.update({ time: candleData.time, value: liveEMA20 });
                  } catch (e) {
                    // Silently handle
                  }
                }
                
                if (liveEMA34 !== null && liveEMA34 !== undefined && ema34SeriesRef.current && indicatorVisibility.ema34) {
                  try {
                    ema34SeriesRef.current.update({ time: candleData.time, value: liveEMA34 });
                  } catch (e) {
                    // Silently handle
                  }
                }
                
                // Update VWAP series
                if (liveVWAP !== null && !isNaN(liveVWAP) && vwapSeriesRef.current && indicatorVisibility.vwap) {
                  try {
                    vwapSeriesRef.current.update({ time: candleData.time, value: liveVWAP });
                  } catch (e) {
                    // Silently handle
                  }
                }
                
                // Update RSI series
                if (liveRSI !== null && !isNaN(liveRSI) && rsiSeriesRef.current && indicatorVisibility.rsi14) {
                  try {
                    rsiSeriesRef.current.update({ time: candleData.time, value: liveRSI });
                  } catch (e) {
                    // Silently handle
                  }
                }
                
                // SuperTrend now uses segmented rendering
                // Real-time updates handled by chart re-render when chartData changes
                // No need for manual series updates
                } // End of if (isNewInterval) - indicator chart updates
                
                // Build indicator values for state with calculated live values
                const indicatorValues = {
                  ema_9: liveEMA9,
                  ema_20: liveEMA20,
                  ema_34: liveEMA34,
                  vwap: liveVWAP,
                  rsi_14: liveRSI,
                  supertrend_1: liveST1,
                  supertrend_2: liveST2,
                };
                
                // Create full candle object with all properties for state
                const fullCandleData = {
                  time: candleData.time,
                  timestamp: message.timestamp,
                  open: candleData.open,
                  high: candleData.high,
                  low: candleData.low,
                  close: candleData.close,
                  volume: message.volume || 0,
                  // Use indicator values from last candle for live approximation (recalculated on REST refresh)
                  ...indicatorValues,
                };
                
                // Check if this candle already exists (updating existing live candle)
                const existingIndex = prevCandles.findIndex(c => c.time === candleData.time);
                
                if (existingIndex >= 0) {
                  // Update existing candle
                  const newCandles = [...prevCandles];
                  newCandles[existingIndex] = {
                    ...newCandles[existingIndex],
                    ...fullCandleData,
                  };
                  return newCandles;
                } else {
                  // Add new candle at the end
                  return [...prevCandles, fullCandleData];
                }
              });
              
              // Update live price from the candle's close price
              if (candleData.close !== null && candleData.close !== undefined) {
                const newPrice = Number(candleData.close);
                const oldPrice = previousPriceRef.current;
                setLivePrice(newPrice);
                
                // Calculate price change if we have a previous price
                if (oldPrice !== null && oldPrice !== newPrice) {
                  const change = newPrice - oldPrice;
                  const changePercent = (change / oldPrice) * 100;
                  setLivePriceChange(change);
                  setLivePriceChangePercent(changePercent);
                }
                
                // Update previous price reference
                previousPriceRef.current = newPrice;
                
                // Update lastUpdate timestamp
                setLastUpdate(new Date());
              }
              
              // Update volume if available
              updateVolume(candleData.time, candleData.close, candleData.open, message.volume);
              
              // SuperTrend colors are now updated in real-time within the indicator calculation above
              
              // Log updates periodically to avoid console spam
              updateCountRef.current += 1;
              if (updateCountRef.current % 10 === 0 || updateCountRef.current <= 3) {
                console.log(`📊 Live candle update #${updateCountRef.current}: ${message.timeframe} at ${new Date(message.timestamp).toLocaleTimeString()}, O=${candleData.open}, H=${candleData.high}, L=${candleData.low}, C=${candleData.close}`);
              }
            } catch (chartError) {
              console.error('❌ Error updating live candle in chart:', chartError, 'Message:', message);
            }
            break;
            
          case 'new_candle':
            // A brand new, completed candle has been created at interval boundary
            // This candle is complete and should be added as a new bar
            // The timestamp will be different from the previous candle, so update() will create a new one
            setUpdateCount(prev => prev + 1);
            
            // If chart is not ready, queue the message for later processing
            if (!candlestickSeriesRef.current || !isChartReadyRef.current) {
              console.log('⏳ Chart series not yet initialized. Queuing new_candle message.');
              wsMessageQueueRef.current.push(message);
              // Limit queue size to prevent memory issues
              if (wsMessageQueueRef.current.length > 100) {
                wsMessageQueueRef.current.shift(); // Remove oldest message
              }
              return;
            }
            
            try {
              const candleData = prepareCandleData(message);
              
              if (!candleData) {
                console.warn('⚠️ Invalid candle data in new_candle, skipping:', message);
                return;
              }
              
              // CRITICAL: When a new_candle arrives, it represents a COMPLETED candle
              // The backend sends this when a candle interval completes
              // We need to:
              // 1. Finalize the previous live candle (if it exists with same timestamp)
              // 2. Keep it on the chart (update() will finalize it)
              // 3. The next live_update will have a NEW timestamp for the new forming candle
              
              const existingData = candlestickSeriesRef.current.data();
              const candleExists = existingData.some(c => c.time === candleData.time);
              
              if (candleExists) {
                // Candle already exists - this is the live candle that just completed
                // Update it to finalize it (this keeps it on the chart)
                console.log(`✅ Finalizing completed candle at time ${candleData.time} (${new Date(candleData.time * 1000).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })})`);
                candlestickSeriesRef.current.update(candleData);
              } else {
                // New candle that doesn't exist yet - add it
                // This shouldn't normally happen, but handle it gracefully
                console.log(`🕯️ Adding new completed candle at time ${candleData.time} (${new Date(candleData.time * 1000).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })})`);
                candlestickSeriesRef.current.update(candleData);
              }
              
              // IMPORTANT: Don't update lastLiveCandleRef here!
              // The completed candle should stay on the chart, but the next live_update
              // will have a DIFFERENT timestamp (the new interval start), so it will create a new candle
              // We'll update lastLiveCandleRef when the first live_update arrives for the new candle
              
              // IMPORTANT: Also update historicalCandles state to keep it in sync
              // Set flag to skip full chart update (we already updated via candlestickSeriesRef)
              skipChartUpdateRef.current = true;
              // Must match the baseline used in the updater below — `historicalCandles` in this closure is stale until commit.
              let pendingNewCandleIndicators = null;
              flushSync(() => {
              setHistoricalCandles(prevCandles => {
                if (prevCandles.length === 0) {
                  pendingNewCandleIndicators = null;
                  return prevCandles;
                }
                
                // Get the last completed candle for indicator baseline (needed for both SuperTrend and other indicators)
                const lastCompletedCandle = prevCandles.length >= 2 ? prevCandles[prevCandles.length - 2] : prevCandles[prevCandles.length - 1];
                
                // CRITICAL: Use SuperTrend values from backend if available (calculated with full historical context)
                // Otherwise, fall back to frontend recalculation
                let liveST1, liveST2;
                
                if (message.supertrend_1 !== undefined && message.supertrend_2 !== undefined && message.indicators_included) {
                  // Backend calculated SuperTrend values are available - use them!
                  liveST1 = message.supertrend_1;
                  liveST2 = message.supertrend_2;
                  console.log(`✅ Using backend-calculated SuperTrend: ST1=${liveST1?.toFixed(2)}, ST2=${liveST2?.toFixed(2)}`);
                } else {
                  // Fallback: Recalculate SuperTrend on frontend (less accurate but better than nothing)
                  if (lastCompletedCandle) {
                    // Recalculate SuperTrend 1 (10, 1.5)
                    liveST1 = calculateLiveSuperTrend(
                      candleData.high,
                      candleData.low,
                      candleData.close,
                      lastCompletedCandle.supertrend_1 ?? null,
                      10,
                      1.5
                    );
                    
                    // Recalculate SuperTrend 2 (10, 2.0)
                    liveST2 = calculateLiveSuperTrend(
                      candleData.high,
                      candleData.low,
                      candleData.close,
                      lastCompletedCandle.supertrend_2 ?? null,
                      10,
                      2.0
                    );
                    
                    // Update refs for next calculation
                    prevHighRef.current = candleData.high;
                    prevLowRef.current = candleData.low;
                    prevCloseRef.current = candleData.close;
                    
                    console.log(`⚠️ Backend SuperTrend not available, using frontend calculation: ST1=${liveST1?.toFixed(2)}, ST2=${liveST2?.toFixed(2)}`);
                  } else {
                    // Fallback to previous values if no baseline
                    liveST1 = lastCompletedCandle?.supertrend_1 ?? null;
                    liveST2 = lastCompletedCandle?.supertrend_2 ?? null;
                  }
                }
                
                // Recalculate other indicators for the completed candle
                let liveEMA9, liveEMA20, liveEMA34, liveVWAP, liveRSI;
                
                if (lastCompletedCandle) {
                  liveEMA9 = calculateLiveEMA(lastCompletedCandle.ema_9, candleData.close, 9);
                  liveEMA20 = calculateLiveEMA(lastCompletedCandle.ema_20, candleData.close, 20);
                  liveEMA34 = calculateLiveEMA(lastCompletedCandle.ema_34, candleData.close, 34);
                  liveVWAP = calculateLiveVWAP(candleData.close, message.volume || 0);
                  
                  const prevClose = lastCompletedCandle.close;
                  liveRSI = prevClose ? calculateLiveRSI(candleData.close, prevClose) : lastCompletedCandle.rsi_14;
                } else {
                  liveEMA9 = lastCompletedCandle?.ema_9 ?? null;
                  liveEMA20 = lastCompletedCandle?.ema_20 ?? null;
                  liveEMA34 = lastCompletedCandle?.ema_34 ?? null;
                  liveVWAP = lastCompletedCandle?.vwap ?? null;
                  liveRSI = lastCompletedCandle?.rsi_14 ?? null;
                }
                
                const indicatorValues = {
                  ema_9: liveEMA9,
                  ema_20: liveEMA20,
                  ema_34: liveEMA34,
                  vwap: liveVWAP,
                  rsi_14: liveRSI,
                  supertrend_1: liveST1,
                  supertrend_2: liveST2,
                };
                
                // Create full candle object with all properties for state
                const fullCandleData = {
                  time: candleData.time,
                  timestamp: message.timestamp,
                  open: candleData.open,
                  high: candleData.high,
                  low: candleData.low,
                  close: candleData.close,
                  volume: message.volume || 0,
                  // Use recalculated indicator values
                  ...indicatorValues,
                };
                
                // Check if this candle already exists (finalizing existing live candle)
                const existingIndex = prevCandles.findIndex(c => c.time === candleData.time);
                
                if (existingIndex >= 0) {
                  // Update existing candle to finalize it with recalculated indicators
                  const newCandles = [...prevCandles];
                  newCandles[existingIndex] = {
                    ...newCandles[existingIndex],
                    ...fullCandleData,
                  };
                  
                  // Update indicator refs for next calculation
                  lastIndicatorValuesRef.current = {
                    supertrend_1: liveST1,
                    supertrend_2: liveST2,
                  };
                  
                  console.log(`✅ Completed candle finalized with recalculated SuperTrend: ST1=${liveST1?.toFixed(2)}, ST2=${liveST2?.toFixed(2)}`);
                  
                  pendingNewCandleIndicators = {
                    candleData,
                    liveEMA9,
                    liveEMA20,
                    liveEMA34,
                    liveVWAP,
                    liveRSI,
                    liveST1,
                    liveST2,
                  };
                  return newCandles;
                } else {
                  // Add new completed candle at the end
                  // Update indicator refs
                  lastIndicatorValuesRef.current = {
                    supertrend_1: liveST1,
                    supertrend_2: liveST2,
                  };
                  
                  console.log(`✅ New completed candle added with recalculated SuperTrend: ST1=${liveST1?.toFixed(2)}, ST2=${liveST2?.toFixed(2)}`);
                  
                  pendingNewCandleIndicators = {
                    candleData,
                    liveEMA9,
                    liveEMA20,
                    liveEMA34,
                    liveVWAP,
                    liveRSI,
                    liveST1,
                    liveST2,
                  };
                  return [...prevCandles, fullCandleData];
                }
              });
              });
              
              // Update live price from the new candle's close price
              if (candleData.close !== null && candleData.close !== undefined) {
                const newPrice = Number(candleData.close);
                setLivePrice(newPrice);
                
                // Calculate price change if we have a previous price
                if (previousPriceRef.current !== null && previousPriceRef.current !== newPrice) {
                  const change = newPrice - previousPriceRef.current;
                  const changePercent = (change / previousPriceRef.current) * 100;
                  setLivePriceChange(change);
                  setLivePriceChangePercent(changePercent);
                }
                
                // Update previous price reference
                previousPriceRef.current = newPrice;
                
                // Update lastUpdate timestamp
                setLastUpdate(new Date());
              }
              
              // Update volume if available
              updateVolume(candleData.time, candleData.close, candleData.open, message.volume);
              
              // ========== CRITICAL: Update all indicator series when new candle completes ==========
              // Use values computed in the same functional update as historicalCandles (pendingNewCandleIndicators).
              
              if (pendingNewCandleIndicators) {
                const {
                  candleData: indCandle,
                  liveEMA9: newEMA9,
                  liveEMA20: newEMA20,
                  liveEMA34: newEMA34,
                  liveVWAP: newVWAP,
                  liveRSI: newRSI,
                  liveST1: newST1Value,
                  liveST2: newST2Value,
                } = pendingNewCandleIndicators;
                
                if (ema9SeriesRef.current && indicatorVisibility.ema9 && newEMA9 !== null && newEMA9 !== undefined && !isNaN(newEMA9)) {
                  try {
                    ema9SeriesRef.current.update({ time: indCandle.time, value: newEMA9 });
                    console.log(`📈 EMA 9 updated: ${newEMA9.toFixed(2)}`);
                  } catch (e) { /* Series might not be ready */ }
                }
                
                if (ema20SeriesRef.current && indicatorVisibility.ema20 && newEMA20 !== null && newEMA20 !== undefined && !isNaN(newEMA20)) {
                  try {
                    ema20SeriesRef.current.update({ time: indCandle.time, value: newEMA20 });
                    console.log(`📈 EMA 20 updated: ${newEMA20.toFixed(2)}`);
                  } catch (e) { /* Series might not be ready */ }
                }
                
                if (ema34SeriesRef.current && indicatorVisibility.ema34 && newEMA34 !== null && newEMA34 !== undefined && !isNaN(newEMA34)) {
                  try {
                    ema34SeriesRef.current.update({ time: indCandle.time, value: newEMA34 });
                    console.log(`📈 EMA 34 updated: ${newEMA34.toFixed(2)}`);
                  } catch (e) { /* Series might not be ready */ }
                }
                
                if (vwapSeriesRef.current && indicatorVisibility.vwap && newVWAP !== null && newVWAP !== undefined && !isNaN(newVWAP)) {
                  try {
                    vwapSeriesRef.current.update({ time: indCandle.time, value: newVWAP });
                    console.log(`📈 VWAP updated: ${newVWAP.toFixed(2)}`);
                  } catch (e) { /* Series might not be ready */ }
                }
                
                if (rsiSeriesRef.current && indicatorVisibility.rsi14 && newRSI !== null && newRSI !== undefined && !isNaN(newRSI)) {
                  try {
                    rsiSeriesRef.current.update({ time: indCandle.time, value: newRSI });
                    console.log(`📈 RSI 14 updated: ${newRSI.toFixed(2)}`);
                  } catch (e) { /* Series might not be ready */ }
                }

                if (
                  maRibbonSeriesRef.current &&
                  indicatorVisibilityRef.current.maRibbon &&
                  maRibbonFastEmaRef.current != null &&
                  maRibbonSlowEmaRef.current != null
                ) {
                  const ribbonFast = calculateLiveEMA(
                    maRibbonFastEmaRef.current,
                    indCandle.close,
                    MA_RIBBON_FAST_LEN
                  );
                  const ribbonSlow = calculateLiveEMA(
                    maRibbonSlowEmaRef.current,
                    indCandle.close,
                    MA_RIBBON_SLOW_LEN
                  );
                  try {
                    maRibbonSeriesRef.current.update({
                      time: indCandle.time,
                      fast: ribbonFast,
                      slow: ribbonSlow,
                    });
                    maRibbonFastEmaRef.current = ribbonFast;
                    maRibbonSlowEmaRef.current = ribbonSlow;
                  } catch (e) { /* Series might not be ready */ }
                }
                
                if (supertrend1SeriesRef.current && Array.isArray(supertrend1SeriesRef.current) && indicatorVisibility.supertrend1 && newST1Value !== null && newST1Value !== undefined) {
                  try {
                    const isBullish = indCandle.close > newST1Value;
                    const newPoint = { time: indCandle.time, value: newST1Value };
                    const lastSeries = supertrend1SeriesRef.current[supertrend1SeriesRef.current.length - 1];
                    if (lastSeries) {
                      lastSeries.update(newPoint);
                      console.log(`📈 SuperTrend 1 updated: ${newST1Value.toFixed(2)} (${isBullish ? 'Bullish' : 'Bearish'})`);
                    }
                  } catch (e) { console.log('SuperTrend 1 update skipped:', e.message); }
                }
                
                if (supertrend2SeriesRef.current && Array.isArray(supertrend2SeriesRef.current) && indicatorVisibility.supertrend2 && newST2Value !== null && newST2Value !== undefined) {
                  try {
                    const isBullish = indCandle.close > newST2Value;
                    const newPoint = { time: indCandle.time, value: newST2Value };
                    const lastSeries = supertrend2SeriesRef.current[supertrend2SeriesRef.current.length - 1];
                    if (lastSeries) {
                      lastSeries.update(newPoint);
                      console.log(`📈 SuperTrend 2 updated: ${newST2Value.toFixed(2)} (${isBullish ? 'Bullish' : 'Bearish'})`);
                    }
                  } catch (e) { console.log('SuperTrend 2 update skipped:', e.message); }
                }
              }
              
              // ========== END Indicator series updates ==========
              
              console.log(`🕯️ New completed candle received and added to chart: ${message.timeframe} at ${new Date(message.timestamp).toLocaleTimeString()}, O=${candleData.open}, H=${candleData.high}, L=${candleData.low}, C=${candleData.close}`);
              
              // NOTE: No API refresh needed - all indicators are updated directly via .update()
              // This provides smooth real-time updates without chart flicker
              console.log('✅ All indicators updated via WebSocket - no chart refresh needed');
              
            } catch (chartError) {
              console.error('❌ Error adding new candle to chart:', chartError, 'Message:', message);
            }
            break;
            
          case 'update_candle':
            // Legacy message type: existing candle being updated with new high/low/close
            setUpdateCount(prev => prev + 1);
            
            // If chart is not ready, queue the message for later processing
            if (!candlestickSeriesRef.current || !isChartReadyRef.current) {
              console.log('⏳ Chart series not yet initialized. Queuing update_candle message.');
              wsMessageQueueRef.current.push(message);
              // Limit queue size to prevent memory issues
              if (wsMessageQueueRef.current.length > 100) {
                wsMessageQueueRef.current.shift(); // Remove oldest message
              }
              return;
            }
            
            try {
              const candleData = prepareCandleData(message);
              
              if (!candleData) {
                console.warn('⚠️ Invalid candle data in update_candle, skipping:', message);
                return;
              }
              
              // Update existing candle (updates the last bar without creating new one)
              candlestickSeriesRef.current.update(candleData);
              
              // Store reference for future updates
              lastLiveCandleRef.current = { ...candleData };
              
              // IMPORTANT: Also update historicalCandles state to keep it in sync
              // Set flag to skip full chart update (we already updated via candlestickSeriesRef)
              skipChartUpdateRef.current = true;
              setHistoricalCandles(prevCandles => {
                if (prevCandles.length === 0) return prevCandles;
                
                // Check if this candle already exists
                const candleIndex = prevCandles.findIndex(c => c.time === candleData.time);
                const existingCandle = candleIndex >= 0 ? prevCandles[candleIndex] : prevCandles[prevCandles.length - 1];
                
                // Get indicator values from the existing candle being updated
                const indicatorValues = existingCandle ? {
                  ema_9: existingCandle.ema_9,
                  ema_20: existingCandle.ema_20,
                  ema_34: existingCandle.ema_34,
                  vwap: existingCandle.vwap,
                  rsi_14: existingCandle.rsi_14,
                  supertrend_1: existingCandle.supertrend_1,
                  supertrend_2: existingCandle.supertrend_2,
                } : {};
                
                // Create full candle object with all properties for state
                const fullCandleData = {
                  time: candleData.time,
                  timestamp: message.timestamp,
                  open: candleData.open,
                  high: candleData.high,
                  low: candleData.low,
                  close: candleData.close,
                  volume: message.volume || 0,
                  // Preserve indicator values from existing candle (recalculated on REST refresh)
                  ...indicatorValues,
                };
                
                if (candleIndex >= 0) {
                  // Update existing candle
                  const newCandles = [...prevCandles];
                  newCandles[candleIndex] = {
                    ...newCandles[candleIndex],
                    ...fullCandleData,
                  };
                  return newCandles;
                } else {
                  // Add new candle at the end
                  return [...prevCandles, fullCandleData];
                }
              });
              
              // Update volume if available
              updateVolume(candleData.time, candleData.close, candleData.open, message.volume);
            } catch (chartError) {
              console.error('❌ Error updating candle in chart:', chartError, 'Message:', message);
            }
            break;
            
          case 'live_price':
            // Update live price state for display (separate from candle updates)
            if (message.price !== undefined && message.price !== null) {
              const newPrice = Number(message.price);
              const oldPrice = previousPriceRef.current;
              setLivePrice(newPrice);
              
              // Calculate price change if we have a previous price
              if (oldPrice !== null && oldPrice !== newPrice) {
                const change = newPrice - oldPrice;
                const changePercent = (change / oldPrice) * 100;
                setLivePriceChange(change);
                setLivePriceChangePercent(changePercent);
              }
              
              // Update previous price reference
              previousPriceRef.current = newPrice;
              
              // Always update lastUpdate timestamp when we receive a live_price message
              setLastUpdate(new Date());
            }
            if (message.change !== undefined) {
              setLivePriceChange(Number(message.change));
            }
            if (message.change_percent !== undefined) {
              setLivePriceChangePercent(Number(message.change_percent));
            }
            if (message.timestamp) {
              // Use the timestamp from message if available, otherwise use current time
              setLastUpdate(new Date(message.timestamp));
            }
            break;
            
          case 'nifty_signal':
            // Scheduler broadcast — update Latest Signal; refresh analysis run after.
            if (message.signal) {
              const incomingSignal = message.signal;
              const wsTimestamp = message.timestamp || incomingSignal.timestamp || new Date().toISOString();

              if (isNonExecutableSignalPayload(incomingSignal)) {
                setCooldownNotice(buildCooldownNoticeFromSignal(incomingSignal, wsTimestamp));
                setAnalysisStatus('no_active_signal');
                setResult((prevResult) => {
                  if (prevResult?.status === 'active_signal' && prevResult.signal) {
                    return prevResult;
                  }
                  const dbSignal = latestSignalRef.current;
                  const dbTs = signalTimestampRef.current;
                  if (isRecentDbSavedSignal(dbSignal, dbTs)) {
                    return buildPreservedActiveResult(
                      { strategy_progress: prevResult?.strategy_progress },
                      dbSignal
                    );
                  }
                  return {
                    status: 'no_active_signal',
                    message: incomingSignal.save_failed
                      ? 'Signal could not be saved to database.'
                      : 'Signal blocked by cooldown/restrictions.',
                  };
                });
                setLastAnalysisTime(new Date(wsTimestamp));
                break;
              }

              setCooldownNotice(null);
              const newId = incomingSignal?.signal_id;
              notifyIfNewSavedSignalId(newId);

              setLatestSignal(incomingSignal);
              setSignalTimestamp(wsTimestamp);
              lastActiveSignalResultRef.current = {
                status: 'active_signal',
                signal: incomingSignal,
              };
              console.log('✅ New signal received via WebSocket:', incomingSignal.signal_type);

              setResult((prevResult) => {
                if (prevResult?.signal && newId && prevResult.signal.signal_id === newId) {
                  return {
                    ...prevResult,
                    status: 'active_signal',
                    signal: { ...prevResult.signal, ...incomingSignal },
                  };
                }
                return {
                  status: 'active_signal',
                  signal: incomingSignal,
                };
              });
              setAnalysisStatus('active_signal');
              setLastAnalysisTime(new Date(wsTimestamp));
              fetchLatestAnalysisRunRef.current?.();
            }
            break;
            
          case 'heartbeat':
            // Acknowledge the heartbeat silently to keep the connection alive
            // This keeps the connection alive and the console clean
            // Uncomment the line below for debugging if needed
            // console.debug("💓 Heartbeat received, connection is alive.");
            break;
            
          case 'error':
            console.error('❌ WebSocket error:', message.message);
            // Do not overwrite chart UI with transient WS errors.
            // Keep chart visible and rely on connection badge/reconnect flow instead.
            break;
            
          default:
            // Handle other message types or log unhandled types for debugging
            console.log('📨 Received unhandled message type:', message.type, message);
            break;
        }
      } catch (e) {
        console.error('❌ Error parsing WebSocket message:', e, 'Raw event data:', event.data);
      }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        console.error('❌ WebSocket error details:', {
          type: error.type,
          target: error.target,
          readyState: ws.readyState,
          url: wsUrl.replace(/token=[^&]*/, 'token=***')
        });
        setWsConnected(false);
        // Don't reconnect immediately on error - wait for onclose event
      };

      // Helper function to attempt reconnection with exponential backoff
      const attemptReconnect = () => {
        // Clear any existing reconnection timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        // Don't reconnect if auto-refresh is disabled or connection was manually closed
        if (!autoRefresh || isManualCloseRef.current) {
          console.log('⏭️ Skipping reconnection: auto-refresh disabled or manual close');
          return;
        }
        
        // Don't reconnect if we already have an active connection
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          console.log('⏭️ Skipping reconnection: connection already active');
          return;
        }
        
        // Calculate delay with exponential backoff (max 60 seconds)
        const baseDelay = 2000; // Start with 2 seconds
        const maxDelay = 60000; // Max 60 seconds
        const delay = Math.min(baseDelay * Math.pow(2, reconnectAttemptsRef.current), maxDelay);
        reconnectAttemptsRef.current += 1;
        setReconnectAttempts(reconnectAttemptsRef.current); // Update UI
        
        console.log(`🔄 Will attempt to reconnect WebSocket in ${delay/1000} seconds (attempt ${reconnectAttemptsRef.current})...`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          // Check again before reconnecting
          if (!autoRefresh || isManualCloseRef.current) {
            console.log('⏭️ Reconnection cancelled: auto-refresh disabled or manual close');
            reconnectTimeoutRef.current = null;
            return;
          }
          
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            console.log('⏭️ Reconnection cancelled: connection already active');
            reconnectTimeoutRef.current = null;
            return;
          }
          
          console.log(`🔄 Reconnecting WebSocket (attempt ${reconnectAttemptsRef.current})...`);
          reconnectTimeoutRef.current = null;
          
          // Trigger reconnection by incrementing reconnectTrigger
          // This will cause the useEffect to run again and create a new connection
          setReconnectTrigger(prev => prev + 1);
        }, delay);
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket disconnected', event.code, event.reason);
        setWsConnected(false);
        
        // Clear heartbeat interval
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        
        // Clear the WebSocket reference
        wsRef.current = null;
        
        // Only attempt to reconnect if it wasn't a manual close
        // Code 1000 = normal closure, 1001 = going away
        const shouldReconnect = !isManualCloseRef.current && event.code !== 1000 && event.code !== 1001;
        
        if (shouldReconnect) {
          attemptReconnect();
        } else {
          console.log('⏭️ Not reconnecting: manual close or normal closure');
          reconnectAttemptsRef.current = 0; // Reset attempts on manual close
          setReconnectAttempts(0); // Reset UI counter
        }
      };
    }, 1000); // Delay connection by 1 second to allow initial data to load

    return () => {
      clearTimeout(connectionDelay);
      // Clear reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // Clear heartbeat interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      // Mark as manual close when component unmounts
      isManualCloseRef.current = true;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setWsConnected(false);
      reconnectAttemptsRef.current = 0; // Reset attempts
      setReconnectAttempts(0); // Reset UI counter
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedTimeframe, reconnectTrigger]); // Include reconnectTrigger to force reconnection

  /**
   * Initialize previous price from chart data when it loads.
   * This allows us to calculate price changes when live updates arrive.
   * Also update indicator refs for WebSocket to access without stale closures.
   */
  useEffect(() => {
    if (chartData?.current_price && !priceInitializedRef.current) {
      const initialPrice = Number(chartData.current_price);
      previousPriceRef.current = initialPrice;
      priceInitializedRef.current = true;
      // Initialize livePrice with the chart data price
      // Using functional form to avoid needing livePrice in dependencies
      setLivePrice(prevPrice => prevPrice ?? initialPrice);
    }
    
    // Update indicator refs so WebSocket can access them without closure issues
    if (chartData && chartData.chart_data && chartData.chart_data.length > 0) {
      const lastCandle = chartData.chart_data[chartData.chart_data.length - 1];
      lastIndicatorValuesRef.current = {
        supertrend_1: lastCandle?.supertrend_1 ?? null,
        supertrend_2: lastCandle?.supertrend_2 ?? null,
      };
      
      // Initialize VWAP cumulative values from chart data
      if (lastCandle?.vwap && lastCandle?.volume) {
        // Estimate cumulative values (will be precise after first REST refresh)
        vwapCumulativePVRef.current = lastCandle.vwap * lastCandle.volume;
        vwapCumulativeVolumeRef.current = lastCandle.volume;
      }
      
      // Initialize RSI smoothed values (for incremental RSI calculation)
      // We'll use the last RSI value as baseline and initialize gain/loss refs
      if (lastCandle?.rsi_14 !== null && lastCandle?.rsi_14 !== undefined) {
        // Approximate initial smoothed values from RSI
        // RSI = 100 - (100 / (1 + RS)), where RS = AvgGain / AvgLoss
        const rsi = lastCandle.rsi_14;
        const rs = (100 - rsi) > 0 ? rsi / (100 - rsi) : 1;
        // Assume equal smoothing for simplicity (will normalize on REST refresh)
        rsiGainRef.current = rs / 2;
        rsiLossRef.current = 0.5;
      }
      
      // Initialize refs for SuperTrend calculation
      if (lastCandle) {
        prevHighRef.current = lastCandle.high;
        prevLowRef.current = lastCandle.low;
        prevCloseRef.current = lastCandle.close;
        
        // Estimate ATR from SuperTrend if available
        // SuperTrend = HL2 ± (Factor × ATR)
        if (lastCandle.supertrend_1 !== null && lastCandle.supertrend_1 !== undefined) {
          const hl2 = (lastCandle.high + lastCandle.low) / 2;
          // Approximate ATR: ATR ≈ (SuperTrend - HL2) / Factor
          const estimatedATR = Math.abs(lastCandle.supertrend_1 - hl2) / 1.5;
          atrRef.current = estimatedATR;
        }
      }
    }
  }, [chartData]);

  /**
   * Periodic full chart refresh via REST API.
   * 
   * This runs less frequently than WebSocket updates because:
   * - WebSocket handles real-time price updates (every second)
   * - REST API provides complete historical data with recalculated indicators
   * 
   * The periodic refresh ensures:
   * - Indicators (EMA, VWAP, RSI) are recalculated with latest data
   * - New completed candles are included
   * - Pattern detection runs on fresh data
   */
  useEffect(() => {
    // Clear existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    if (!autoRefresh) {
      return;
    }

    // Determine refresh interval based on timeframe (less frequent since WebSocket handles live updates)
    const getRefreshInterval = (timeframe) => {
      switch (timeframe) {
        case '5minute':
          return 300000; // 5 minutes (WebSocket handles live updates every second)
        case '15minute':
          return 600000; // 10 minutes
        case '30minute':
          return 900000; // 15 minutes
        case '60minute':
          return 1800000; // 30 minutes
        case 'day':
          return 3600000; // 1 hour
        case 'week':
          return 7200000; // 2 hours
        default:
          return 300000; // Default 5 minutes
      }
    };

    const interval = getRefreshInterval(selectedTimeframe);
    
    // Set up periodic refresh for complete chart data via REST API
    // This ensures indicators and patterns are recalculated with latest historical data
    refreshIntervalRef.current = setInterval(() => {
      console.log(`🔄 Periodic full chart refresh via REST API (timeframe: ${selectedTimeframe})`);
      fetchChartData(selectedTimeframe, true); // updateOnly = true to avoid loading spinner
    }, interval);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [autoRefresh, selectedTimeframe, fetchChartData]);

  const renderResult = () => {
    if (!result) return null;
    
    // Handle BTST / Overnight Gap Outlook (15:10-15:30 IST window)
    if (result.status === 'overnight_outlook' && result.outlook) {
      const outlook = result.outlook;
      const gapDirection = outlook.gap_direction || 'Neutral';
      const confidence = outlook.confidence || 'Medium';
      
      return (
        <div className="mt-4">
          <div className={`p-4 rounded border ${
            gapDirection === 'Gap Up' ? 'bg-green-50 border-green-200' :
            gapDirection === 'Gap Down' ? 'bg-red-50 border-red-200' :
            'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <span>🌙</span>
                <span>Overnight Gap Outlook (BTST)</span>
              </h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                confidence === 'High' ? 'bg-green-200 text-green-800' :
                confidence === 'Medium' ? 'bg-yellow-200 text-yellow-800' :
                'bg-gray-200 text-gray-800'
              }`}>
                {confidence} Confidence
              </span>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Expected Gap:</span>
                <span className={`text-lg font-bold ${
                  gapDirection === 'Gap Up' ? 'text-green-600' :
                  gapDirection === 'Gap Down' ? 'text-red-600' :
                  'text-gray-600'
                }`}>
                  {gapDirection === 'Gap Up' ? '📈' : gapDirection === 'Gap Down' ? '📉' : '➡️'} {gapDirection}
                </span>
              </div>
              
              {outlook.reasoning && (
                <div className="mt-3 p-3 rounded bg-white border border-gray-200">
                  <div className="font-semibold text-sm text-gray-700 mb-1">Analysis:</div>
                  <div className="text-sm text-gray-600">{outlook.reasoning}</div>
                </div>
              )}
              
              {outlook.factors && outlook.factors.length > 0 && (
                <div className="mt-2">
                  <div className="font-semibold text-sm text-gray-700 mb-1">Key Factors:</div>
                  <ul className="list-disc list-inside text-sm text-gray-600">
                    {outlook.factors.map((factor, idx) => (
                      <li key={idx}>{factor}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                💡 <strong>BTST Strategy:</strong> This outlook helps plan overnight positions. 
                Active during last 20 minutes of market (3:10-3:30 PM IST).
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Handle different response statuses
    if (result.status === 'no_signal' || result.status === 'no_active_signal') {
      // Check if we have strategy progress to display
      if (result.strategy_progress && result.strategy_progress.length > 0) {
        return (
          <div className="mt-4">
            <div className="mb-3 p-3 rounded bg-blue-50 border border-blue-200">
              <div className="text-sm font-medium text-blue-900">
                📊 Monitoring {result.strategy_progress.length} Strategy Setup(s)
              </div>
              <div className="text-xs text-blue-700 mt-1">
                {result.message || 'Watching for high-probability setups...'}
              </div>
            </div>
            
            {/* Strategy Progress Cards */}
            <div className="space-y-4">
              {result.strategy_progress.map((strategy, idx) => {
                // Check if this is an ARMED predictive signal
                const isArmedSignal = strategy.status === 'ARMED';
                
                return (
                <div 
                  key={idx} 
                  className={`border rounded-lg overflow-hidden shadow-sm ${
                    isArmedSignal 
                      ? 'border-4 border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50 animate-pulse-slow' 
                      : 'border-gray-200 bg-white'
                  }`}
                  style={isArmedSignal ? {
                    boxShadow: '0 0 20px rgba(251, 191, 36, 0.4)',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  } : {}}
                >
                  {/* Strategy Header */}
                  <div className={`px-4 py-3 border-b ${
                    isArmedSignal 
                      ? 'bg-gradient-to-r from-yellow-100 to-orange-100 border-yellow-300' 
                      : 'bg-gradient-to-r from-indigo-50 to-blue-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <h3 className={`text-lg font-semibold ${isArmedSignal ? 'text-orange-900' : 'text-gray-900'}`}>
                        {isArmedSignal && <span className="mr-2">⚡</span>}
                        {strategy.strategy_name}
                      </h3>
                      {isArmedSignal && (
                        <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                          ARMED
                        </span>
                      )}
                    </div>
                    {strategy.status === 'insufficient_data' && (
                      <p className="text-sm text-orange-600 mt-1">⏳ {strategy.message}</p>
                    )}
                    {isArmedSignal && strategy.signal_type && (
                      <div className={`mt-2 text-sm font-semibold ${
                        strategy.signal_type === 'Bullish' ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {strategy.signal_type === 'Bullish' ? '🚀' : '📉'} {strategy.signal_type.toUpperCase()} SETUP FORMING
                      </div>
                    )}
                  </div>
                  
                  {/* ARMED Signal: Show reasoning and trigger levels prominently */}
                  {isArmedSignal && strategy.reasoning && (
                    <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-200">
                      <p className="text-sm font-medium text-gray-900 mb-2">
                        {strategy.reasoning}
                      </p>
                      
                      {/* Trigger Levels */}
                      {strategy.trigger_levels && (
                        <div className="mt-3 p-3 bg-white rounded-lg border-2 border-orange-300">
                          <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                            <span>🎯</span>
                            <span>Watch For:</span>
                          </h4>
                          {strategy.signal_type === 'Bullish' && strategy.trigger_levels.bullish_breakout_above && (
                            <div className="text-lg font-bold text-green-700">
                              Breakout ABOVE: ₹{strategy.trigger_levels.bullish_breakout_above.toFixed(2)}
                            </div>
                          )}
                          {strategy.signal_type === 'Bearish' && strategy.trigger_levels.bearish_breakdown_below && (
                            <div className="text-lg font-bold text-red-700">
                              Breakdown BELOW: ₹{strategy.trigger_levels.bearish_breakdown_below.toFixed(2)}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Additional Metrics */}
                      {(strategy.bb_width_pct || strategy.rsi || strategy.launchpad) && (
                        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                          {strategy.bb_width_pct && (
                            <div className="bg-white p-2 rounded border border-yellow-200">
                              <div className="text-gray-600">BB Width</div>
                              <div className="font-bold text-orange-700">{strategy.bb_width_pct}%</div>
                            </div>
                          )}
                          {strategy.rsi && (
                            <div className="bg-white p-2 rounded border border-yellow-200">
                              <div className="text-gray-600">RSI</div>
                              <div className="font-bold text-orange-700">{strategy.rsi}</div>
                            </div>
                          )}
                          {strategy.launchpad && (
                            <div className="bg-white p-2 rounded border border-yellow-200">
                              <div className="text-gray-600">Launchpad</div>
                              <div className="font-bold text-orange-700">{strategy.launchpad.name}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Setup Conditions (for ARMED signals, show as confirmation checklist) */}
                  {isArmedSignal && strategy.setup_conditions && Object.keys(strategy.setup_conditions).length > 0 && (
                    <div className="px-4 py-3 bg-gradient-to-br from-green-50 to-emerald-50 border-b border-green-200">
                      <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                        <span>✅</span>
                        <span>All Conditions Met:</span>
                      </h4>
                      <ul className="space-y-2">
                        {Object.entries(strategy.setup_conditions).map(([condition, data]) => {
                          const isMet = typeof data === 'object' ? data.met : data;
                          const details = typeof data === 'object' ? data.details : '';
                          return (
                            <li key={condition} className={`flex items-start gap-2 text-sm ${isMet ? 'text-green-700' : 'text-gray-600'}`}>
                              <span className="text-lg">{isMet ? '✅' : '⏳'}</span>
                              <div className="flex-1">
                                <div className="font-medium">{condition}</div>
                                {details && <div className="text-xs opacity-75 mt-0.5">{details}</div>}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  
                  {/* Condition Checklist (for regular monitoring strategies) */}
                  {!isArmedSignal && strategy.bullish_setup && Object.keys(strategy.bullish_setup).length > 0 && (
                    <div className="grid md:grid-cols-2 gap-4 p-4">
                      {/* Bullish Setup */}
                      <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                        <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                          <span className="text-lg">📈</span>
                          <span>Bullish (CE) Setup</span>
                          <span className="ml-auto text-sm px-2 py-0.5 bg-green-200 rounded-full">
                            {strategy.bullish_score || 0}/4
                          </span>
                        </h4>
                        <ul className="space-y-2">
                          {Object.entries(strategy.bullish_setup).map(([condition, data]) => {
                            const isMet = typeof data === 'object' ? data.met : data;
                            const details = typeof data === 'object' ? data.details : '';
                            return (
                              <li key={condition} className={`flex items-start gap-2 text-sm ${isMet ? 'text-green-700' : 'text-gray-600'}`}>
                                <span className="text-lg">{isMet ? '✅' : '⏳'}</span>
                                <div className="flex-1">
                                  <div className="font-medium">{condition}</div>
                                  {details && <div className="text-xs opacity-75 mt-0.5">{details}</div>}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      
                      {/* Bearish Setup */}
                      <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                        <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                          <span className="text-lg">📉</span>
                          <span>Bearish (PE) Setup</span>
                          <span className="ml-auto text-sm px-2 py-0.5 bg-red-200 rounded-full">
                            {strategy.bearish_score || 0}/4
                          </span>
                        </h4>
                        <ul className="space-y-2">
                          {Object.entries(strategy.bearish_setup).map(([condition, data]) => {
                            const isMet = typeof data === 'object' ? data.met : data;
                            const details = typeof data === 'object' ? data.details : '';
                            return (
                              <li key={condition} className={`flex items-start gap-2 text-sm ${isMet ? 'text-red-700' : 'text-gray-600'}`}>
                                <span className="text-lg">{isMet ? '✅' : '⏳'}</span>
                                <div className="flex-1">
                                  <div className="font-medium">{condition}</div>
                                  {details && <div className="text-xs opacity-75 mt-0.5">{details}</div>}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  {/* Pivot Levels Info (if available) */}
                  {!isArmedSignal && strategy.pivot_levels && (
                    <div className="px-4 pb-3 border-t border-gray-100">
                      <div className="text-xs text-gray-600 mt-2">
                        <span className="font-semibold">Pivot Levels:</span>{' '}
                        R3: ₹{strategy.pivot_levels.R3?.toFixed(2)} | R2: ₹{strategy.pivot_levels.R2?.toFixed(2)} | R1: ₹{strategy.pivot_levels.R1?.toFixed(2)} | 
                        S1: ₹{strategy.pivot_levels.S1?.toFixed(2)} | S2: ₹{strategy.pivot_levels.S2?.toFixed(2)} | S3: ₹{strategy.pivot_levels.S3?.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        );
      }
      
      // Fallback if no strategy progress
      return (
        <div className="mt-4">
          <div className="p-4 rounded bg-yellow-50 border border-yellow-200 text-yellow-800">
            {result.message || 'No active signal right now. Try again later.'}
          </div>
        </div>
      );
    }
    
    if (result.status === 'data_error') {
      return (
        <div className="mt-4">
          <div className="p-4 rounded bg-red-50 border border-red-200 text-red-800">
            <div className="font-semibold">Data Error</div>
            <div className="mt-1">{result.message || 'An error occurred while fetching data.'}</div>
          </div>
        </div>
      );
    }
    
    // Check if we have an active signal with valid signal data
    if (result.status !== 'active_signal' || !result.signal) {
      // This shouldn't happen if we handled all cases above, but just in case
      return (
        <div className="mt-4">
          <div className="p-4 rounded bg-yellow-50 border border-yellow-200 text-yellow-800">
            {result.message || 'No active signal available.'}
          </div>
        </div>
      );
    }
    
    // Extract signal data - handle both nested and flat structures
    const signal = result.signal || result;
    
    // Debug logging
    console.log('📊 Signal data:', signal);
    console.log('📊 Result data:', result);
    
    const entryPriceRaw = signal?.entry_price || result?.entry_price;
    const stopLossRaw = signal?.stop_loss || result?.stop_loss;
    const targets = signal?.targets || result?.targets || [];
    
    const confluenceFibActive = signal?.strategy_type === 'confluence_fib_intraday';
    const maRibbonActive = isMaRibbonSignal(signal);
    const maRibbonLevels = maRibbonActive ? getMaRibbonTradeLevels(signal) : null;

    // Extract target price.
    // For Confluence Fib Intraday, TP1 must come from target_1 (DB/backtest-consistent),
    // not generic target_price which can be a display fallback from other strategies.
    // For MA Ribbon, prefer target_1 / option map over generic target_price (+10 fallback).
    let firstTarget =
      (maRibbonActive
        ? (maRibbonLevels?.target1 ?? signal?.target_1 ?? result?.target_1)
        : confluenceFibActive
        ? (signal?.target_1 ?? result?.target_1 ?? signal?.target1 ?? result?.target1)
        : (signal?.target_price ??
           result?.target_price ??
           signal?.target_1 ??
           result?.target_1 ??
           signal?.target1 ??
           result?.target1));
    if (!firstTarget) {
      if (Array.isArray(targets) && targets.length > 0) {
        // If targets[0] is an object, extract the value property
        const targetObj = targets[0];
        firstTarget = (typeof targetObj === 'object' && targetObj !== null && 'value' in targetObj) 
          ? targetObj.value 
          : targetObj;
      } else {
        firstTarget = result?.target || null;
      }
    }

    const entryPrice = maRibbonLevels?.entry ?? entryPriceRaw;
    const stopLoss = maRibbonLevels?.stopLoss ?? stopLossRaw;
    
    const riskReward = signal?.risk_reward_ratio || result?.risk_reward_ratio;
    const signalType = signal?.signal_type || result?.signal_type;
    const instrument = signal?.instrument || result?.instrument;
    const timestamp = signal?.timestamp || result?.timestamp;
    const timestampDisplayIST = (() => {
      if (timestamp == null || timestamp === '') return 'N/A';
      const d = new Date(timestamp);
      if (Number.isNaN(d.getTime())) return String(timestamp);
      return `${d.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })} IST`;
    })();
    const reasoning = signal?.reasoning || result?.reasoning;
    const warnings = signal?.warnings || result?.warnings || [];

    const scoreBreakdown = signal?.score_breakdown || result?.score_breakdown || null;
    const tp2 = maRibbonLevels?.target2 ?? signal?.target_2 ?? result?.target_2;
    const tp3 = maRibbonLevels?.target3 ?? signal?.target_3 ?? result?.target_3;
    const entryIndexFmt = maRibbonActive
      ? formatMaRibbonPriceWithIndex(entryPrice, maRibbonLevels?.indexEntry)
      : null;
    const targetIndexFmt = maRibbonActive
      ? formatMaRibbonPriceWithIndex(firstTarget, maRibbonLevels?.indexTarget1)
      : null;
    const stopIndexFmt = maRibbonActive
      ? formatMaRibbonPriceWithIndex(stopLoss, maRibbonLevels?.indexStopLoss)
      : null;
    
    // Validate that we have at least entry price before showing signal
    if (!entryPrice && !stopLoss && !firstTarget) {
      console.warn('⚠️ Signal object exists but has no valid price data:', signal);
      return (
        <div className="mt-4">
          <div className="p-4 rounded bg-yellow-50 border border-yellow-200 text-yellow-800">
            Signal detected but price data is incomplete. Please try again.
          </div>
        </div>
      );
    }
    
    return (
      <div className="mt-4">
        <div className="p-4 rounded bg-green-50 border border-green-200">
          <div className="text-sm text-gray-500">Timestamp: {timestampDisplayIST}</div>
          <div className="mt-2 text-lg font-semibold">{signalType || 'Signal'}</div>
          <div className="mt-1">Instrument: <span className="font-mono">{instrument || 'N/A'}</span></div>
          <div className="mt-1">
            Entry:{' '}
            {maRibbonActive && entryIndexFmt ? (
              <span>
                {entryIndexFmt.primary}
                {entryIndexFmt.indexLine ? (
                  <span className="block text-xs text-gray-500 mt-0.5">{entryIndexFmt.indexLine}</span>
                ) : null}
              </span>
            ) : (
              <>₹{entryPrice ? Number(entryPrice).toFixed(2) : 'N/A'}</>
            )}
          </div>
          <div className="mt-1">
            Target:{' '}
            {maRibbonActive && targetIndexFmt ? (
              <span>
                {targetIndexFmt.primary}
                {targetIndexFmt.indexLine ? (
                  <span className="block text-xs text-gray-500 mt-0.5">{targetIndexFmt.indexLine}</span>
                ) : null}
              </span>
            ) : (
              <>₹{firstTarget ? Number(firstTarget).toFixed(2) : 'N/A'}</>
            )}
          </div>
          <div className="mt-1">
            Stop Loss:{' '}
            {maRibbonActive && stopIndexFmt ? (
              <span>
                {stopIndexFmt.primary}
                {stopIndexFmt.indexLine ? (
                  <span className="block text-xs text-gray-500 mt-0.5">{stopIndexFmt.indexLine}</span>
                ) : null}
              </span>
            ) : (
              <>₹{stopLoss ? Number(stopLoss).toFixed(2) : 'N/A'}</>
            )}
          </div>
          {maRibbonActive ? (
            <div className="mt-3 p-3 rounded bg-indigo-50 border border-indigo-200">
              <div className="text-sm font-semibold text-indigo-900 mb-1">MA Ribbon Cloud ATR</div>
              <div className="text-sm font-semibold text-indigo-900">
                TP1 {targetIndexFmt?.primary || 'N/A'}
                {typeof tp2 === 'number' ? ` | TP2 ₹${tp2.toFixed(2)}` : ''}
                {typeof tp3 === 'number' ? ` | TP3 ₹${tp3.toFixed(2)}` : ''}
              </div>
              {(maRibbonLevels?.indexTarget2 != null || maRibbonLevels?.indexTarget3 != null) && (
                <div className="mt-1 text-xs text-indigo-800">
                  {maRibbonLevels?.indexTarget2 != null ? `NIFTY TP2: ₹${maRibbonLevels.indexTarget2.toFixed(2)}` : ''}
                  {maRibbonLevels?.indexTarget2 != null && maRibbonLevels?.indexTarget3 != null ? ' | ' : ''}
                  {maRibbonLevels?.indexTarget3 != null ? `NIFTY TP3: ₹${maRibbonLevels.indexTarget3.toFixed(2)}` : ''}
                </div>
              )}
            </div>
          ) : null}
          {confluenceFibActive ? (
            <div className="mt-3 p-3 rounded bg-blue-50 border border-blue-200">
              <div className="text-sm font-semibold text-blue-900 mb-1">Confluence Fib Intraday</div>
              <div className="text-xs text-blue-800">
                Scores:{' '}
                Long {typeof scoreBreakdown?.long_score === 'number' ? scoreBreakdown.long_score : 0}/6 |{' '}
                Short {typeof scoreBreakdown?.short_score === 'number' ? scoreBreakdown.short_score : 0}/6 |{' '}
                Trigger {typeof scoreBreakdown?.trigger_score === 'number' ? scoreBreakdown.trigger_score : 0}
              </div>
              <div className="mt-2 text-sm font-semibold text-blue-900">
                TP1 ₹{firstTarget ? Number(firstTarget).toFixed(2) : 'N/A'} | TP2 ₹{typeof tp2 === 'number' ? tp2.toFixed(2) : 'N/A'} | TP3 ₹{typeof tp3 === 'number' ? tp3.toFixed(2) : 'N/A'}
              </div>
            </div>
          ) : null}
          {riskReward && <div className="mt-1">RR: {riskReward}</div>}
          
          {/* ML Confidence Display (when available) */}
          {(signal?.ml_success_probability || result?.ml_success_probability) && (
            <div className="mt-4 p-3 rounded bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-purple-900">🤖 AI Confidence</span>
                <span className={`text-sm font-bold ${
                  (signal?.ml_success_probability || result?.ml_success_probability || 0) >= 0.7 ? 'text-green-600' :
                  (signal?.ml_success_probability || result?.ml_success_probability || 0) >= 0.5 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {((signal?.ml_success_probability || result?.ml_success_probability || 0) * 100).toFixed(0)}%
                </span>
              </div>
              
              {/* Confidence bar */}
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
                <div 
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    (signal?.ml_success_probability || result?.ml_success_probability || 0) >= 0.7 ? 'bg-green-500' :
                    (signal?.ml_success_probability || result?.ml_success_probability || 0) >= 0.5 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${(signal?.ml_success_probability || result?.ml_success_probability || 0) * 100}%` }}
                ></div>
              </div>
              
              {/* ML Predictions */}
              <div className="grid grid-cols-2 gap-2 text-xs text-purple-800">
                {(signal?.ml_expected_profit_pct || result?.ml_expected_profit_pct) && (
                  <div>
                    <span className="font-medium">Expected Profit:</span>{' '}
                    <span className={`font-bold ${
                      (signal?.ml_expected_profit_pct || result?.ml_expected_profit_pct || 0) > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {(signal?.ml_expected_profit_pct || result?.ml_expected_profit_pct || 0) > 0 ? '+' : ''}
                      {(signal?.ml_expected_profit_pct || result?.ml_expected_profit_pct || 0).toFixed(1)}%
                    </span>
                  </div>
                )}
                {(signal?.ml_expected_holding_minutes || result?.ml_expected_holding_minutes) && (
                  <div>
                    <span className="font-medium">Expected Hold:</span>{' '}
                    <span className="font-bold">
                      {signal?.ml_expected_holding_minutes || result?.ml_expected_holding_minutes} min
                    </span>
                  </div>
                )}
              </div>
              
              <div className="mt-2 text-xs text-purple-600 italic">
                Based on {signal?.training_signal_count || 'historical'} similar signals
              </div>
            </div>
          )}
          
          {reasoning && (
            <div className="mt-3 text-sm text-gray-700">
              {reasoning.bias && <div>Bias: {reasoning.bias}</div>}
              {reasoning.trigger && <div>Trigger: {reasoning.trigger}</div>}
            </div>
          )}
          {Array.isArray(warnings) && warnings.length > 0 && (
            <div className="mt-3 p-3 rounded bg-red-50 border border-red-200 text-red-700">
              {warnings.map((w, idx) => <div key={idx}>• {w}</div>)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const ema9SeriesRef = useRef(null);
  const ema20SeriesRef = useRef(null);
  const ema34SeriesRef = useRef(null);
  const maRibbonSeriesRef = useRef(null);
  const maRibbonFastEmaRef = useRef(null);
  const maRibbonSlowEmaRef = useRef(null);
  const vwapSeriesRef = useRef(null);
  const supertrend1SeriesRef = useRef(null);
  const supertrend2SeriesRef = useRef(null);
  const rsiSeriesRef = useRef(null);
  const lastLiveCandleRef = useRef(null); // Store last live candle for state management
  const pivotLinesRef = useRef([]); // Store pivot price line references
  const fbrtLinesRef = useRef([]); // Store FBRT price line references
  const dynamicSrLinesRef = useRef([]); // Store dynamic SR price line references
  const fibTargetsLinesRef = useRef([]); // Store Confluence Fib TP/SL price lines
  const fibZoneAreaSeriesRef = useRef(null); // Store Confluence Fib shaded risk zone

  useEffect(() => {
    // Use historicalCandles if available, otherwise fall back to chartData.chart_data
    const candlesToUse = historicalCandles.length > 0 ? historicalCandles : (chartData?.chart_data || []);
    
    // Debug logging
    if (chartData || historicalCandles.length > 0) {
      console.log('📊 Chart data available:', {
        hasChartData: !!chartData,
        chartDataLength: chartData?.chart_data?.length || 0,
        historicalCandlesLength: historicalCandles.length,
        usingHistorical: historicalCandles.length > 0,
        hasContainer: !!chartContainerRef.current,
        chartLoading
      });
    }
    
    if (!chartContainerRef.current) {
      console.log('⏳ Waiting for chart container to be mounted...');
      return;
    }
    
    if (candlesToUse.length === 0) {
      console.log('⏳ Waiting for chart data...', {
        hasChartData: !!chartData,
        hasChartDataArray: !!chartData?.chart_data,
        historicalCandlesLength: historicalCandles.length
      });
      return;
    }

    // If chart already exists, just update the data instead of recreating
    if (chartRef.current && candlestickSeriesRef.current) {
      // Skip full chart update if only live candle is being updated
      // (WebSocket already updated the chart directly via candlestickSeriesRef)
      if (skipChartUpdateRef.current) {
        console.debug('⏭️ Skipping full chart update - only live candle updated via WebSocket');
        skipChartUpdateRef.current = false; // Reset flag
        return;
      }
      
      // Reset live candle ref when chart data is refreshed
      lastLiveCandleRef.current = null;
      
      // Update existing chart with new data
      // Backend now sends Unix timestamps in seconds in the 'time' field
      // Use it directly without any conversion
      const convertTime = (candle) => {
        // Backend always sends 'time' as a Unix timestamp in seconds (number)
        if (candle.time !== undefined && candle.time !== null) {
          if (typeof candle.time === 'number') {
            // Direct passthrough - backend sends Unix timestamp in seconds
            return candle.time;
          }
          // Fallback: if it's a string, try to parse it (shouldn't happen with new backend)
          if (typeof candle.time === 'string') {
            const parsed = parseFloat(candle.time);
            if (!isNaN(parsed)) {
              return parsed;
            }
          }
        }
        return null;
      };

      // Use historicalCandles if available, otherwise use chartData.chart_data
      const sourceCandles = historicalCandles.length > 0 ? historicalCandles : chartData.chart_data;
      
      // Update candlestick data
      const candlestickData = sourceCandles
        .map((candle) => {
          const time = convertTime(candle);
          if (!time) return null;
          return {
            time,
            open: parseFloat(candle.open) || 0,
            high: parseFloat(candle.high) || 0,
            low: parseFloat(candle.low) || 0,
            close: parseFloat(candle.close) || 0,
          };
        })
        .filter(c => c !== null && c.open > 0 && c.high > 0 && c.low > 0 && c.close > 0)
        .sort((a, b) => {
          // Sort by time to ensure chronological order
          // Both should be numbers (Unix timestamps in seconds) from backend
          const timeA = typeof a.time === 'number' ? a.time : 0;
          const timeB = typeof b.time === 'number' ? b.time : 0;
          return timeA - timeB;
        });

      // Update volume data
      const volumeData = sourceCandles
        .map((candle) => {
          const time = convertTime(candle);
          if (!time) return null;
          return {
            time,
            value: parseFloat(candle.volume) || 0,
            color: parseFloat(candle.close) >= parseFloat(candle.open) 
              ? 'rgba(0, 150, 0, 0.5)' 
              : 'rgba(255, 0, 0, 0.5)',
          };
        })
        .filter(v => v !== null && v.value > 0);

      // Update EMA 9 data
      const ema9Data = chartData.chart_data
        .map((candle) => {
          const time = convertTime(candle);
          if (!time || candle.ema_9 === null || candle.ema_9 === undefined) return null;
          return {
            time,
            value: parseFloat(candle.ema_9),
          };
        })
        .filter(e => e !== null && !isNaN(e.value));

      // Update EMA 20 data
      const ema20Data = chartData.chart_data
        .map((candle) => {
          const time = convertTime(candle);
          if (!time || candle.ema_20 === null || candle.ema_20 === undefined) return null;
          return {
            time,
            value: parseFloat(candle.ema_20),
          };
        })
        .filter(e => e !== null && !isNaN(e.value));

      // Update EMA 34 data
      const ema34Data = chartData.chart_data
        .map((candle) => {
          const time = convertTime(candle);
          if (!time || candle.ema_34 === null || candle.ema_34 === undefined) return null;
          return {
            time,
            value: parseFloat(candle.ema_34),
          };
        })
        .filter(e => e !== null && !isNaN(e.value));

      // Update VWAP data
      const vwapData = chartData.chart_data
        .map((candle) => {
          const time = convertTime(candle);
          if (!time || candle.vwap === null || candle.vwap === undefined) return null;
          return {
            time,
            value: parseFloat(candle.vwap),
          };
        })
        .filter(v => v !== null && !isNaN(v.value));

      // Update RSI data
      const rsiData = chartData.chart_data
        .map((candle) => {
          const time = convertTime(candle);
          if (!time || candle.rsi_14 === null || candle.rsi_14 === undefined) return null;
          return {
            time,
            value: parseFloat(candle.rsi_14),
          };
        })
        .filter(e => e !== null && !isNaN(e.value));

      // SuperTrend data is now prepared using segmentation logic above
      // No need for old color determination helper function

      // Update series data
      if (candlestickSeriesRef.current) {
        candlestickSeriesRef.current.setData(candlestickData);
        
        // Initialize lastLiveCandleRef with the last candle from the data
        // This ensures live updates will update the correct candle
        if (candlestickData.length > 0) {
          const lastCandle = candlestickData[candlestickData.length - 1];
          lastLiveCandleRef.current = { ...lastCandle };
          console.log(`📊 Initialized lastLiveCandleRef with last candle: time=${lastCandle.time}, close=${lastCandle.close}`);
        }
      }
      if (volumeSeriesRef.current) {
        volumeSeriesRef.current.setData(volumeData);
      }
      if (rsiSeriesRef.current && rsiData.length > 0) {
        try {
          rsiSeriesRef.current.setData(rsiData);
          if (typeof rsiSeriesRef.current.applyOptions === 'function') {
            rsiSeriesRef.current.applyOptions({ visible: indicatorVisibility.rsi14 });
          } else if (typeof rsiSeriesRef.current.setVisible === 'function') {
            rsiSeriesRef.current.setVisible(indicatorVisibility.rsi14);
          }
        } catch (error) {
          console.error('Error updating RSI series:', error);
        }
      }
      if (ema9SeriesRef.current && ema9Data.length > 0) {
        try {
          ema9SeriesRef.current.setData(ema9Data);
          if (typeof ema9SeriesRef.current.applyOptions === 'function') {
            ema9SeriesRef.current.applyOptions({ visible: indicatorVisibility.ema9 });
          } else if (typeof ema9SeriesRef.current.setVisible === 'function') {
            ema9SeriesRef.current.setVisible(indicatorVisibility.ema9);
          }
        } catch (error) {
          console.error('Error updating EMA 9 series:', error);
        }
      }
      if (ema20SeriesRef.current && ema20Data.length > 0) {
        try {
          ema20SeriesRef.current.setData(ema20Data);
          if (typeof ema20SeriesRef.current.applyOptions === 'function') {
            ema20SeriesRef.current.applyOptions({ visible: indicatorVisibility.ema20 });
          } else if (typeof ema20SeriesRef.current.setVisible === 'function') {
            ema20SeriesRef.current.setVisible(indicatorVisibility.ema20);
          }
        } catch (error) {
          console.error('Error updating EMA 20 series:', error);
        }
      }

      if (ema34SeriesRef.current && ema34Data.length > 0) {
        try {
          ema34SeriesRef.current.setData(ema34Data);
          if (typeof ema34SeriesRef.current.applyOptions === 'function') {
            ema34SeriesRef.current.applyOptions({ visible: indicatorVisibility.ema34 });
          } else if (typeof ema34SeriesRef.current.setVisible === 'function') {
            ema34SeriesRef.current.setVisible(indicatorVisibility.ema34);
          }
        } catch (error) {
          console.error('Error updating EMA 34 series:', error);
        }
      }

      if (maRibbonSeriesRef.current) {
        try {
          const ribbonData = buildMaRibbonDataFromCandles(chartData.chart_data, convertTime);
          if (ribbonData.length > 0) {
            maRibbonSeriesRef.current.setData(ribbonData);
            const lastRibbon = ribbonData[ribbonData.length - 1];
            maRibbonFastEmaRef.current = lastRibbon.fast;
            maRibbonSlowEmaRef.current = lastRibbon.slow;
            if (typeof maRibbonSeriesRef.current.applyOptions === 'function') {
              maRibbonSeriesRef.current.applyOptions({ visible: indicatorVisibility.maRibbon });
            }
          }
        } catch (error) {
          console.error('Error updating MA Ribbon series:', error);
        }
      }
      if (vwapSeriesRef.current && vwapData.length > 0) {
        try {
          vwapSeriesRef.current.setData(vwapData);
          if (typeof vwapSeriesRef.current.applyOptions === 'function') {
            vwapSeriesRef.current.applyOptions({ visible: indicatorVisibility.vwap });
          } else if (typeof vwapSeriesRef.current.setVisible === 'function') {
            vwapSeriesRef.current.setVisible(indicatorVisibility.vwap);
          }
        } catch (error) {
          console.error('Error updating VWAP series:', error);
        }
      }
      // SuperTrend series are now managed as segmented arrays
      // Chart re-render handles updates automatically


      // Update pattern annotations when filters change
      // Note: Lightweight Charts doesn't support removing individual price lines,
      // so we need to recreate the candlestick series to update annotations
      // Save the current visible range to preserve user's view when toggling patterns
      let savedVisibleRange = null;
      try {
        if (chartRef.current && chartRef.current.timeScale()) {
          savedVisibleRange = chartRef.current.timeScale().getVisibleRange();
        }
      } catch (e) {
        console.debug('Could not save visible range:', e);
      }

      // Update pattern annotations when data or filters change
      // If showPatterns is disabled, remove all pattern price lines by recreating the candlestick series
      if (!showPatterns && candlestickSeriesRef.current) {
        // Remove all price lines by recreating the candlestick series
        const existingData = candlestickSeriesRef.current.data();
        const newCandlestickSeries = chartRef.current.addCandlestickSeries({
          upColor: '#26a69a',
          downColor: '#ef5350',
          borderVisible: false,
          wickUpColor: '#26a69a',
          wickDownColor: '#ef5350',
        });
        newCandlestickSeries.setData(existingData);
        chartRef.current.removeSeries(candlestickSeriesRef.current);
        candlestickSeriesRef.current = newCandlestickSeries;
      }
      // Only render if showPatterns is enabled
      // Note: We need to remove existing price lines and re-add filtered ones
      // Since Lightweight Charts doesn't support removing individual price lines,
      // we'll recreate the chart when filters change (handled by dependencies)
      // For data updates with same filters, we still need to update annotations
      else if (showPatterns && chartData.pattern_annotations && Array.isArray(chartData.pattern_annotations) && candlestickSeriesRef.current) {
        // Filter annotations based on current filters
        const filteredAnnotations = chartData.pattern_annotations.filter((patternAnnotation) => {
          const patternName = patternAnnotation.pattern_name || 'Unknown Pattern';
          const signal = patternAnnotation.signal || 'Neutral';
          const status = patternAnnotation.status || 'Forming';

          if (patternFilters.patternType !== 'all' && patternName !== patternFilters.patternType) {
            return false;
          }
          if (patternFilters.status !== 'all' && status !== patternFilters.status) {
            return false;
          }
          if (patternFilters.signal !== 'all' && signal !== patternFilters.signal) {
            return false;
          }
          if (patternFilters.startDate || patternFilters.endDate) {
            const patternDate = patternAnnotation.timestamp 
              ? new Date(patternAnnotation.timestamp * 1000) 
              : patternAnnotation.date 
              ? new Date(patternAnnotation.date) 
              : null;
            if (patternDate) {
              if (patternFilters.startDate) {
                const startDate = new Date(patternFilters.startDate);
                startDate.setHours(0, 0, 0, 0);
                if (patternDate < startDate) return false;
              }
              if (patternFilters.endDate) {
                const endDate = new Date(patternFilters.endDate);
                endDate.setHours(23, 59, 59, 999);
                if (patternDate > endDate) return false;
              }
            }
          }
          return true;
        });

        // Remove all existing price lines by recreating the candlestick series
        // This is necessary because Lightweight Charts doesn't support removing individual price lines
        const existingData = candlestickSeriesRef.current.data();
        const newCandlestickSeries = chartRef.current.addCandlestickSeries({
          upColor: '#26a69a',
          downColor: '#ef5350',
          borderVisible: false,
          wickUpColor: '#26a69a',
          wickDownColor: '#ef5350',
        });
        newCandlestickSeries.setData(existingData);
        chartRef.current.removeSeries(candlestickSeriesRef.current);
        candlestickSeriesRef.current = newCandlestickSeries;

        // Add filtered pattern annotations
        filteredAnnotations.forEach((patternAnnotation) => {
          const patternName = patternAnnotation.pattern_name || 'Unknown Pattern';
          const signal = patternAnnotation.signal || 'Neutral';
          const priceLines = patternAnnotation.price_lines || [];
          
          priceLines.forEach((priceLine) => {
            try {
              const priceLineOptions = {
                price: priceLine.price,
                color: priceLine.color || (signal === 'Bullish' ? '#22c55e' : signal === 'Bearish' ? '#ef4444' : '#6b7280'),
                lineWidth: priceLine.lineWidth || 2,
                lineStyle: priceLine.lineStyle || 0,
                axisLabelVisible: priceLine.axisLabelVisible !== false,
                title: priceLine.title || `${patternName} ${priceLine.price.toFixed(2)}`,
              };
              newCandlestickSeries.createPriceLine(priceLineOptions);
            } catch (error) {
              console.error(`❌ Error adding price line for ${patternName}:`, error);
            }
          });
        });
      }

      // Restore the visible range after pattern updates to preserve user's view
      if (savedVisibleRange && chartRef.current && chartRef.current.timeScale()) {
        try {
          chartRef.current.timeScale().setVisibleRange(savedVisibleRange);
          console.debug('📊 Restored visible range after pattern update');
        } catch (e) {
          console.debug('Could not restore visible range:', e);
        }
      }

      // Don't auto-scroll on data updates - let user control the view
      return; // Exit early, chart already exists and updated
    }

    // Clean up previous chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }
    
    // Reset chart ready flag when recreating chart
    isChartReadyRef.current = false;
    wsMessageQueueRef.current = []; // Clear message queue when recreating chart

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#ffffff' },
        textColor: '#333',
      },
      width: chartContainerRef.current.clientWidth,
      height: 500,
      grid: {
        vertLines: { color: '#e0e0e0' },
        horzLines: { color: '#e0e0e0' },
      },
      rightPriceScale: {
        borderColor: '#cccccc',
      },
      timeScale: {
        borderColor: '#cccccc',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 12,
        barSpacing: 6, // Better spacing between bars for clearer visibility
        minBarSpacing: 2, // Ensure minimum visibility
        fixLeftEdge: false,
        fixRightEdge: false,
        // Custom time formatter to explicitly display times in IST (Asia/Kolkata)
        tickMarkFormatter: (time, tickMarkType, locale) => {
          // `time` is a Unix timestamp in seconds (UTC)
          const date = new Date(time * 1000); // Convert to milliseconds
          
          // For intraday charts, show date at 9:15 AM (market open) and time for other ticks
          if (['5minute', '15minute', '30minute', '60minute'].includes(selectedTimeframe)) {
            // Get IST time components
            const istFormatter = new Intl.DateTimeFormat('en-IN', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              timeZone: 'Asia/Kolkata'
            });
            
            const dateFormatter = new Intl.DateTimeFormat('en-IN', {
              day: '2-digit',
              month: 'short',
              year: '2-digit',
              timeZone: 'Asia/Kolkata'
            });
            
            // Parse the formatted time to get hours and minutes
            const istTimeString = istFormatter.format(date);
            const [hours, minutes] = istTimeString.split(':').map(Number);
            
            // Show date at 9:15 AM (market open time)
            if (hours === 9 && minutes === 15) {
              return dateFormatter.format(date);
            }
            
            // For other times, show only time (HH:MM) in IST
            return istTimeString;
          }
          
          // For daily/weekly charts, show date
          return new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: 'short',
            year: '2-digit',
            timeZone: 'Asia/Kolkata'
          }).format(date);
        },
      },
      // Custom localization for crosshair time display
      localization: {
        timeFormatter: (time) => {
          // `time` is a Unix timestamp in seconds (UTC)
          const date = new Date(time * 1000); // Convert to milliseconds
          
          // For intraday charts, show date and time in IST
          if (['5minute', '15minute', '30minute', '60minute'].includes(selectedTimeframe)) {
            return new Intl.DateTimeFormat('en-IN', {
              day: '2-digit',
              month: 'short',
              year: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false, // Use 24-hour format
              timeZone: 'Asia/Kolkata'
            }).format(date);
          }
          
          // For daily/weekly charts, show date only
          return new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: 'short',
            year: '2-digit',
            timeZone: 'Asia/Kolkata'
          }).format(date);
        },
      },
    });

    chartRef.current = chart;

    // Helper function to convert time to chart format
    // Backend now sends Unix timestamps in seconds in the 'time' field
    // Use it directly without any conversion
    const convertTime = (candle) => {
      // Backend always sends 'time' as a Unix timestamp in seconds (number)
      if (candle.time !== undefined && candle.time !== null) {
        if (typeof candle.time === 'number') {
          // Direct passthrough - backend sends Unix timestamp in seconds
          return candle.time;
        }
        // Fallback: if it's a string, try to parse it (shouldn't happen with new backend)
        if (typeof candle.time === 'string') {
          const parsed = parseFloat(candle.time);
          if (!isNaN(parsed)) {
            return parsed;
          }
        }
      }
      return null;
    };

    // Prepare candlestick data
    const candlestickData = chartData.chart_data
      .map((candle) => {
        const time = convertTime(candle);
        if (!time) return null;
        return {
          time,
          open: parseFloat(candle.open) || 0,
          high: parseFloat(candle.high) || 0,
          low: parseFloat(candle.low) || 0,
          close: parseFloat(candle.close) || 0,
        };
      })
      .filter(c => c !== null && c.open > 0 && c.high > 0 && c.low > 0 && c.close > 0)
      .sort((a, b) => {
        // Sort by time to ensure chronological order
        // Both should be numbers (Unix timestamps in seconds) from backend
        const timeA = typeof a.time === 'number' ? a.time : 0;
        const timeB = typeof b.time === 'number' ? b.time : 0;
        return timeA - timeB;
      });

    // Prepare volume data
    const volumeData = chartData.chart_data
      .map((candle) => {
        const time = convertTime(candle);
        if (!time) return null;
        return {
          time,
          value: parseFloat(candle.volume) || 0,
          color: parseFloat(candle.close) >= parseFloat(candle.open) 
            ? 'rgba(0, 150, 0, 0.5)' 
            : 'rgba(255, 0, 0, 0.5)',
        };
      })
      .filter(v => v !== null && v.value > 0);

    // Prepare EMA 9 data
    const ema9Data = chartData.chart_data
      .map((candle) => {
        const time = convertTime(candle);
        if (!time || candle.ema_9 === null || candle.ema_9 === undefined) return null;
        return {
          time,
          value: parseFloat(candle.ema_9),
        };
      })
      .filter(e => e !== null && !isNaN(e.value));

    // Prepare EMA 20 data
    const ema20Data = chartData.chart_data
      .map((candle) => {
        const time = convertTime(candle);
        if (!time || candle.ema_20 === null || candle.ema_20 === undefined) return null;
        return {
          time,
          value: parseFloat(candle.ema_20),
        };
      })
      .filter(e => e !== null && !isNaN(e.value));

    // Prepare EMA 34 data
    const ema34Data = chartData.chart_data
      .map((candle) => {
        const time = convertTime(candle);
        if (!time || candle.ema_34 === null || candle.ema_34 === undefined) return null;
        return {
          time,
          value: parseFloat(candle.ema_34),
        };
      })
      .filter(e => e !== null && !isNaN(e.value));

    // Prepare VWAP data
    const vwapData = chartData.chart_data
      .map((candle) => {
        const time = convertTime(candle);
        if (!time || candle.vwap === null || candle.vwap === undefined) return null;
        return {
          time,
          value: parseFloat(candle.vwap),
        };
      })
      .filter(v => v !== null && !isNaN(v.value));

    // Prepare RSI 14 data
    const rsiData = chartData.chart_data
      .map((candle) => {
        const time = convertTime(candle);
        if (!time || candle.rsi_14 === null || candle.rsi_14 === undefined) return null;
        return {
          time,
          value: parseFloat(candle.rsi_14),
        };
      })
      .filter(e => e !== null && !isNaN(e.value));

    // === NEW: Professional SuperTrend Segmentation Logic ===
    // Segments SuperTrend into separate up/down trend arrays for proper rendering
    const prepareSuperTrendSegments = (data, stField, directionField) => {
      const upTrendSegments = [];
      const downTrendSegments = [];
      
      let currentUpSegment = [];
      let currentDownSegment = [];
      
      // Check if direction field exists (for backward compatibility)
      const hasDirectionData = data.length > 0 && data[0][directionField] !== undefined;
      
      if (!hasDirectionData) {
        console.warn(`⚠️ Direction field '${directionField}' not found. Using fallback: single-color line.`);
        // Fallback: Create a single segment with all data (will be colored based on last value)
        const fallbackSegment = [];
        for (let i = 0; i < data.length; i++) {
          const candle = data[i];
          const time = convertTime(candle);
          if (!time || candle[stField] === null || candle[stField] === undefined) continue;
          fallbackSegment.push({ time, value: parseFloat(candle[stField]) });
        }
        
        // Determine color based on last candle (temporary until backend sends direction)
        if (fallbackSegment.length > 0 && data[data.length - 1]) {
          const lastCandle = data[data.length - 1];
          const isUptrend = parseFloat(lastCandle.close || 0) >= parseFloat(lastCandle[stField] || 0);
          if (isUptrend) {
            upTrendSegments.push(fallbackSegment);
          } else {
            downTrendSegments.push(fallbackSegment);
          }
        }
        
        return { upTrendSegments, downTrendSegments };
      }
      
      // Normal segmentation with direction data
      for (let i = 0; i < data.length; i++) {
        const candle = data[i];
        const time = convertTime(candle);
        
        if (!time || candle[stField] === null || candle[stField] === undefined || 
            candle[directionField] === null || candle[directionField] === undefined) {
          continue;
        }
        
        const point = { time, value: parseFloat(candle[stField]) };
        const direction = parseInt(candle[directionField], 10);
        
        if (direction === 1) { // Bullish trend (Green)
          // If we were in a downtrend, end the red segment
          if (currentDownSegment.length > 0) {
            // Add connecting point to bridge segments
            currentDownSegment.push(point);
            downTrendSegments.push(currentDownSegment);
            currentDownSegment = [];
          }
          // Add point to current green segment
          currentUpSegment.push(point);
        } else if (direction === -1) { // Bearish trend (Red)
          // If we were in an uptrend, end the green segment
          if (currentUpSegment.length > 0) {
            // Add connecting point to bridge segments
            currentUpSegment.push(point);
            upTrendSegments.push(currentUpSegment);
            currentUpSegment = [];
          }
          // Add point to current red segment
          currentDownSegment.push(point);
        }
      }
      
      // Add any remaining segments
      if (currentUpSegment.length > 0) upTrendSegments.push(currentUpSegment);
      if (currentDownSegment.length > 0) downTrendSegments.push(currentDownSegment);
      
      return { upTrendSegments, downTrendSegments };
    };
    
    // Use historicalCandles if available (includes WebSocket-updated candles with backend-calculated SuperTrend)
    // Otherwise fall back to chartData.chart_data
    const supertrendSourceCandles = historicalCandles.length > 0 ? historicalCandles : (chartData?.chart_data || []);
    
    // Prepare segmented data for SuperTrend 1 (10, 1.5)
    const { upTrendSegments: supertrend1Up, downTrendSegments: supertrend1Down } = 
      prepareSuperTrendSegments(supertrendSourceCandles, 'supertrend_1', 'supertrend_1_direction');
    
    // Prepare segmented data for SuperTrend 2 (10, 2.0)
    const { upTrendSegments: supertrend2Up, downTrendSegments: supertrend2Down } = 
      prepareSuperTrendSegments(supertrendSourceCandles, 'supertrend_2', 'supertrend_2_direction');

    // Add candlestick series
    const candlestickSeries = chart.addCandlestickSeries({
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });
    candlestickSeries.setData(candlestickData);
    candlestickSeriesRef.current = candlestickSeries;
    
    // Mark chart as ready and process queued WebSocket messages
    isChartReadyRef.current = true;
    console.log('✅ Chart series initialized. Processing queued WebSocket messages...');
    
    // Process any queued WebSocket messages
    if (wsMessageQueueRef.current.length > 0) {
      console.log(`📬 Processing ${wsMessageQueueRef.current.length} queued WebSocket messages`);
      const queuedMessages = [...wsMessageQueueRef.current];
      wsMessageQueueRef.current = []; // Clear queue
      
      // Process each queued message (simulate WebSocket message handling)
      queuedMessages.forEach((message) => {
        // Process messages directly when chart is ready
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          // Process the messages directly here
          setTimeout(() => {
            // Process live_update and new_candle messages from queue
            if (message.type === 'live_update' || message.type === 'new_candle' || message.type === 'update_candle') {
              if (candlestickSeriesRef.current) {
                const prepareCandleData = (msg) => {
                  const candleTime = msg.timestamp ? msg.timestamp / 1000 : (msg.time ? (typeof msg.time === 'number' ? msg.time : new Date(msg.time).getTime() / 1000) : 0);
                  const sanitizeValue = (val) => {
                    const num = Number(val);
                    return isNaN(num) || num <= 0 ? null : num;
                  };
                  const candleData = {
                    time: candleTime,
                    open: sanitizeValue(msg.open),
                    high: sanitizeValue(msg.high),
                    low: sanitizeValue(msg.low),
                    close: sanitizeValue(msg.close),
                  };
                  if (!candleData.time || !candleData.open || !candleData.high || !candleData.low || !candleData.close) {
                    return null;
                  }
                  return candleData;
                };
                
                const candleData = prepareCandleData(message);
                if (candleData) {
                  candlestickSeriesRef.current.update(candleData);
                  if (message.type === 'live_update') {
                    lastLiveCandleRef.current = { ...candleData };
                  }
                  console.log(`✅ Processed queued ${message.type} message`);
                }
              }
            }
          }, 100);
        }
      });
    }
    
    // Initialize lastLiveCandleRef with the last candle from initial data
    // This ensures live updates will update the correct candle
    if (candlestickData.length > 0) {
      const lastCandle = candlestickData[candlestickData.length - 1];
      lastLiveCandleRef.current = { ...lastCandle };
      console.log(`📊 Initialized lastLiveCandleRef with last candle on chart creation: time=${lastCandle.time}, close=${lastCandle.close}`);
    }

    // Add volume series (in a separate pane)
    const volumeSeries = chart.addHistogramSeries({
      color: '#26a69a',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: 'volume',
      scaleMargins: {
        // Reserve a thin band at the very bottom for volume
        top: 0.88,
        bottom: 0,
      },
    });
    volumeSeries.setData(volumeData);
    volumeSeriesRef.current = volumeSeries;

    // Add RSI (14) line in its own pane
    if (rsiData.length > 0) {
      try {
        const rsiSeries = chart.addLineSeries({
          color: '#26C6DA',
          lineWidth: 1.5,
          priceScaleId: 'rsi',
          title: 'RSI 14',
          priceLineVisible: false,
          lastValueVisible: true,
        });
        if (rsiSeries && typeof rsiSeries.setData === 'function') {
          rsiSeries.setData(rsiData);
          // Configure RSI scale to sit near the bottom of the chart, above volume
          const rsiScale = chart.priceScale('rsi');
          if (rsiScale && typeof rsiScale.applyOptions === 'function') {
            rsiScale.applyOptions({
              scaleMargins: {
                // Main price area:   top ~0, bottom ~0.6
                // RSI area:          0.6–0.86
                // Volume area:       0.88–1.0
                top: 0.6,
                bottom: 0.14,
              },
            });
          }
          if (typeof rsiSeries.applyOptions === 'function') {
            rsiSeries.applyOptions({ visible: indicatorVisibility.rsi14 });
          } else if (typeof rsiSeries.setVisible === 'function') {
            rsiSeries.setVisible(indicatorVisibility.rsi14);
          }
          rsiSeriesRef.current = rsiSeries;
        }
      } catch (error) {
        console.error('Error creating RSI series:', error);
      }
    }

    // Add EMA 9 line
    if (ema9Data.length > 0) {
      try {
        const ema9Series = chart.addLineSeries({
          color: '#2196F3',
          lineWidth: 2,
          title: 'EMA 9',
          priceLineVisible: false,
          lastValueVisible: true,
        });
        if (ema9Series && typeof ema9Series.setData === 'function') {
          ema9Series.setData(ema9Data);
          if (typeof ema9Series.applyOptions === 'function') {
            ema9Series.applyOptions({ visible: indicatorVisibility.ema9 });
          } else if (typeof ema9Series.setVisible === 'function') {
            ema9Series.setVisible(indicatorVisibility.ema9);
          }
          ema9SeriesRef.current = ema9Series;
        }
      } catch (error) {
        console.error('Error creating EMA 9 series:', error);
      }
    }

    // Add EMA 20 line
    if (ema20Data.length > 0) {
      try {
        const ema20Series = chart.addLineSeries({
          color: '#AB47BC',
          lineWidth: 2,
          title: 'EMA 20',
          priceLineVisible: false,
          lastValueVisible: true,
        });
        if (ema20Series && typeof ema20Series.setData === 'function') {
          ema20Series.setData(ema20Data);
          if (typeof ema20Series.applyOptions === 'function') {
            ema20Series.applyOptions({ visible: indicatorVisibility.ema20 });
          } else if (typeof ema20Series.setVisible === 'function') {
            ema20Series.setVisible(indicatorVisibility.ema20);
          }
          ema20SeriesRef.current = ema20Series;
        }
      } catch (error) {
        console.error('Error creating EMA 20 series:', error);
      }
    }

    // Add EMA 34 line
    if (ema34Data.length > 0) {
      try {
        const ema34Series = chart.addLineSeries({
          color: '#FF9800',
          lineWidth: 2,
          title: 'EMA 34',
          priceLineVisible: false,
          lastValueVisible: true,
        });
        if (ema34Series && typeof ema34Series.setData === 'function') {
          ema34Series.setData(ema34Data);
          if (typeof ema34Series.applyOptions === 'function') {
            ema34Series.applyOptions({ visible: indicatorVisibility.ema34 });
          } else if (typeof ema34Series.setVisible === 'function') {
            ema34Series.setVisible(indicatorVisibility.ema34);
          }
          ema34SeriesRef.current = ema34Series;
        }
      } catch (error) {
        console.error('Error creating EMA 34 series:', error);
      }
    }

    // MA Ribbon (EMA 9 / 21 cloud — matches MA Ribbon Cloud ATR strategy)
    const maRibbonData = buildMaRibbonDataFromCandles(chartData.chart_data, convertTime);
    if (maRibbonData.length > 0) {
      try {
        const maRibbonView = new MaRibbonPaneView();
        const maRibbonSeries = chart.addCustomSeries(maRibbonView, {
          title: 'MA Ribbon (9/21)',
          priceLineVisible: false,
          lastValueVisible: false,
          visible: indicatorVisibility.maRibbon,
        });
        if (maRibbonSeries && typeof maRibbonSeries.setData === 'function') {
          maRibbonSeries.setData(maRibbonData);
          if (typeof maRibbonSeries.applyOptions === 'function') {
            maRibbonSeries.applyOptions({ visible: indicatorVisibility.maRibbon });
          }
          maRibbonSeriesRef.current = maRibbonSeries;
          const lastRibbon = maRibbonData[maRibbonData.length - 1];
          maRibbonFastEmaRef.current = lastRibbon.fast;
          maRibbonSlowEmaRef.current = lastRibbon.slow;
        }
      } catch (error) {
        console.error('Error creating MA Ribbon series:', error);
      }
    }

    // Add VWAP line
    if (vwapData.length > 0) {
      try {
        const vwapSeries = chart.addLineSeries({
          color: '#9C27B0',
          lineWidth: 10,
          lineStyle: 1, // Dashed line
          title: 'VWAP',
          priceLineVisible: false,
          lastValueVisible: true,
        });
        if (vwapSeries && typeof vwapSeries.setData === 'function') {
          vwapSeries.setData(vwapData);
          if (typeof vwapSeries.applyOptions === 'function') {
            vwapSeries.applyOptions({ visible: indicatorVisibility.vwap });
          } else if (typeof vwapSeries.setVisible === 'function') {
            vwapSeries.setVisible(indicatorVisibility.vwap);
          }
          vwapSeriesRef.current = vwapSeries;
        }
      } catch (error) {
        console.error('Error creating VWAP series:', error);
      }
    }

    // === NEW: Add SuperTrend 1 as multiple segmented line series ===
    // Each trend segment (up or down) is drawn as a separate line series
    const supertrend1SeriesArray = [];
    const commonLineOptions = {
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false, // Only show on last segment
    };
    
    try {
      // Draw all GREEN (uptrend) segments for SuperTrend 1
      supertrend1Up.forEach((segment, index) => {
        if (segment.length > 0) {
          const upSeries = chart.addLineSeries({
            ...commonLineOptions,
            color: '#00C853', // Green
            title: index === 0 ? 'SuperTrend (10, 1.5)' : '', // Only show title once
            lastValueVisible: index === supertrend1Up.length - 1 && supertrend1Down.length === 0, // Show value on last segment
          });
          upSeries.setData(segment);
          upSeries.applyOptions({ visible: indicatorVisibility.supertrend1 });
          supertrend1SeriesArray.push(upSeries);
        }
      });
      
      // Draw all RED (downtrend) segments for SuperTrend 1
      supertrend1Down.forEach((segment, index) => {
        if (segment.length > 0) {
          const downSeries = chart.addLineSeries({
            ...commonLineOptions,
            color: '#ef4444', // Red
            title: index === 0 && supertrend1Up.length === 0 ? 'SuperTrend (10, 1.5)' : '', // Only show title once
            lastValueVisible: index === supertrend1Down.length - 1, // Show value on last segment
          });
          downSeries.setData(segment);
          downSeries.applyOptions({ visible: indicatorVisibility.supertrend1 });
          supertrend1SeriesArray.push(downSeries);
        }
      });
      
      // Store all series in ref (for visibility toggling)
      supertrend1SeriesRef.current = supertrend1SeriesArray;
      
    } catch (error) {
      console.error('Error creating SuperTrend 1 segmented series:', error);
    }

    // === NEW: Add SuperTrend 2 as multiple segmented line series ===
    const supertrend2SeriesArray = [];
    
    try {
      // Draw all GREEN (uptrend) segments for SuperTrend 2
      supertrend2Up.forEach((segment, index) => {
        if (segment.length > 0) {
          const upSeries = chart.addLineSeries({
            ...commonLineOptions,
            color: '#00C853', // Green
            title: index === 0 ? 'SuperTrend (10, 2.0)' : '', // Only show title once
            lastValueVisible: index === supertrend2Up.length - 1 && supertrend2Down.length === 0, // Show value on last segment
          });
          upSeries.setData(segment);
          upSeries.applyOptions({ visible: indicatorVisibility.supertrend2 });
          supertrend2SeriesArray.push(upSeries);
        }
      });
      
      // Draw all RED (downtrend) segments for SuperTrend 2
      supertrend2Down.forEach((segment, index) => {
        if (segment.length > 0) {
          const downSeries = chart.addLineSeries({
            ...commonLineOptions,
            color: '#ef4444', // Red
            title: index === 0 && supertrend2Up.length === 0 ? 'SuperTrend (10, 2.0)' : '', // Only show title once
            lastValueVisible: index === supertrend2Down.length - 1, // Show value on last segment
          });
          downSeries.setData(segment);
          downSeries.applyOptions({ visible: indicatorVisibility.supertrend2 });
          supertrend2SeriesArray.push(downSeries);
        }
      });
      
      // Store all series in ref (for visibility toggling)
      supertrend2SeriesRef.current = supertrend2SeriesArray;
      
    } catch (error) {
      console.error('Error creating SuperTrend 2 segmented series:', error);
    }

    // Add Pattern Annotations (Price Lines for Key Levels)
    // Only render if showPatterns is enabled
    // Filter annotations based on current filters before rendering
    if (showPatterns && chartData.pattern_annotations && Array.isArray(chartData.pattern_annotations) && chartData.pattern_annotations.length > 0) {
      // Filter pattern annotations based on current filters
      const filteredAnnotations = chartData.pattern_annotations.filter((patternAnnotation) => {
        const patternName = patternAnnotation.pattern_name || 'Unknown Pattern';
        const signal = patternAnnotation.signal || 'Neutral';
        const status = patternAnnotation.status || 'Forming';

        // Filter by pattern type
        if (patternFilters.patternType !== 'all' && patternName !== patternFilters.patternType) {
          return false;
        }

        // Filter by status
        if (patternFilters.status !== 'all' && status !== patternFilters.status) {
          return false;
        }

        // Filter by signal
        if (patternFilters.signal !== 'all' && signal !== patternFilters.signal) {
          return false;
        }

        // Filter by date range (if pattern has timestamp)
        if (patternFilters.startDate || patternFilters.endDate) {
          const patternDate = patternAnnotation.timestamp 
            ? new Date(patternAnnotation.timestamp * 1000) 
            : patternAnnotation.date 
            ? new Date(patternAnnotation.date) 
            : null;
          
          if (patternDate) {
            if (patternFilters.startDate) {
              const startDate = new Date(patternFilters.startDate);
              startDate.setHours(0, 0, 0, 0);
              if (patternDate < startDate) {
                return false;
              }
            }
            if (patternFilters.endDate) {
              const endDate = new Date(patternFilters.endDate);
              endDate.setHours(23, 59, 59, 999);
              if (patternDate > endDate) {
                return false;
              }
            }
          }
        }

        return true;
      });

      console.log(`📊 Rendering ${filteredAnnotations.length} of ${chartData.pattern_annotations.length} filtered pattern annotations on chart`);
      
      filteredAnnotations.forEach((patternAnnotation) => {
        const patternName = patternAnnotation.pattern_name || 'Unknown Pattern';
        const signal = patternAnnotation.signal || 'Neutral';
        const priceLines = patternAnnotation.price_lines || [];
        
        // Create price lines for each key level
        priceLines.forEach((priceLine) => {
          try {
            const priceLineOptions = {
              price: priceLine.price,
              color: priceLine.color || (signal === 'Bullish' ? '#22c55e' : signal === 'Bearish' ? '#ef4444' : '#6b7280'),
              lineWidth: priceLine.lineWidth || 2,
              lineStyle: priceLine.lineStyle || 0, // 0 = solid, 1 = dotted, 2 = dashed
              axisLabelVisible: priceLine.axisLabelVisible !== false,
              title: priceLine.title || `${patternName} ${priceLine.price.toFixed(2)}`,
            };
            
            // Add price line to candlestick series
            candlestickSeries.createPriceLine(priceLineOptions);
            
            console.log(`✅ Added price line: ${priceLineOptions.title} at ${priceLineOptions.price}`);
          } catch (error) {
            console.error(`❌ Error adding price line for ${patternName}:`, error);
          }
        });
      });
      
      console.log(`✅ Successfully rendered ${filteredAnnotations.length} filtered pattern annotations on chart`);
      // Pattern lines are added - no scrolling to preserve user's current view
    } else {
      console.log('ℹ️ No pattern annotations to render');
    }

    // Set optimal visible range for intraday charts
    // Instead of fitContent() which compresses all data, show a reasonable number of candles
    setTimeout(() => {
      if (chartRef.current && candlestickData.length > 0) {
        const timeScale = chartRef.current.timeScale();
        
        // For intraday charts (5min, 15min, etc.), show today's full session
        // Calculate the first candle time of today (9:15 AM IST)
        const now = new Date();
        const today915AM = new Date(now);
        today915AM.setHours(9, 15, 0, 0);
        const today915Timestamp = Math.floor(today915AM.getTime() / 1000);
        
        // Find the first candle that's on or after 9:15 AM today
        const firstTodayIndex = candlestickData.findIndex(c => c.time >= today915Timestamp - (5.5 * 60 * 60)); // Adjust for timezone
        
        if (firstTodayIndex >= 0 && ['5minute', '15minute', '30minute', '60minute'].includes(selectedTimeframe)) {
          // For intraday: show from first candle of today to latest
          const fromTime = candlestickData[Math.max(0, firstTodayIndex)].time;
          const toTime = candlestickData[candlestickData.length - 1].time + 300; // Add 5 min buffer
          
          try {
            timeScale.setVisibleRange({ from: fromTime, to: toTime });
            console.log(`📊 Chart set to today's session: ${new Date(fromTime * 1000).toLocaleTimeString()} to ${new Date(toTime * 1000).toLocaleTimeString()}`);
          } catch (e) {
            // Fallback to fitContent if setVisibleRange fails
            timeScale.fitContent();
            console.log(`📊 Chart fitted (fallback): ${candlestickData.length} candles`);
          }
        } else {
          // For daily/weekly charts or if no today data, use fitContent
          timeScale.fitContent();
          const firstTime = candlestickData[0].time * 1000;
          const lastTime = candlestickData[candlestickData.length - 1].time * 1000;
          console.log(`📊 Chart fitted: ${candlestickData.length} candles from ${new Date(firstTime).toLocaleTimeString()} to ${new Date(lastTime).toLocaleTimeString()}`);
        }
      }
    }, 100);

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
    // indicatorVisibility is intentionally excluded - visibility changes are handled
    // directly via button click handlers that call setVisible() on series
    // patternFilters and showPatterns are included so chart recreates when filters/visibility change to update pattern annotations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, chartLoading, selectedTimeframe, patternFilters, showPatterns]);

  /**
   * Subscribe to chart scroll events for infinite scroll (on-demand historical loading).
   * This effect runs whenever the chart is ready and has historical data.
   */
  useEffect(() => {
    if (!chartRef.current || historicalCandles.length === 0 || !oldestTimestampRef.current) {
      return;
    }

    const timeScale = chartRef.current.timeScale();
    if (!timeScale) {
      return;
    }

    console.log('📊 Setting up infinite scroll subscription for chart');

    // Subscribe to visible time range changes
    const unsubscribe = timeScale.subscribeVisibleTimeRangeChange((timeRange) => {
      if (!timeRange || isLoadingHistoryRef.current || !hasMoreHistoryRef.current) {
        return;
      }

      // timeRange.from is the leftmost visible timestamp (Unix seconds)
      const visibleStartTime = timeRange.from;
      const oldestLoadedTime = oldestTimestampRef.current;

      if (!oldestLoadedTime) {
        return;
      }

      // Calculate visible range
      const visibleRange = timeRange.to - timeRange.from;
      // Trigger fetch when user scrolls within 20% of visible range from the oldest loaded data
      const bufferSeconds = Math.max(visibleRange * 0.2, 300); // At least 5 minutes buffer
      const fetchThreshold = oldestLoadedTime + bufferSeconds; // Add buffer (oldest is earlier, so we add)

      // Check if user has scrolled to near the beginning of loaded data
      // visibleStartTime should be less than or equal to fetchThreshold
      const shouldFetch = visibleStartTime <= fetchThreshold;

      console.log('📊 Infinite scroll check:', {
        visibleStartTime: new Date(visibleStartTime * 1000).toISOString(),
        oldestLoadedTime: new Date(oldestLoadedTime * 1000).toISOString(),
        fetchThreshold: new Date(fetchThreshold * 1000).toISOString(),
        timeDiff: visibleStartTime - oldestLoadedTime,
        shouldFetch: shouldFetch
      });

      if (shouldFetch) {
        // User is scrolling to older data - fetch more
        console.log(`📊 Fetching older data before ${new Date(oldestLoadedTime * 1000).toISOString()}`);
        fetchOlderHistory(selectedTimeframe, oldestLoadedTime);
      }
    });

    // Cleanup: unsubscribe when component unmounts or dependencies change
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
        console.log('📊 Cleaned up infinite scroll subscription');
      }
    };
  }, [historicalCandles, selectedTimeframe, fetchOlderHistory]);

  /**
   * If user turns pivots on while chart came from fast-path (no pivot metadata),
   * fetch enriched payload in background and draw pivots as soon as data arrives.
   */
  useEffect(() => {
    if (!indicatorVisibility.pivots) return;
    if (!chartData?.chart_data?.length) return;
    if (hasPivotLevelData(chartData)) return;
    void fetchBackgroundEnrichedChartData(selectedTimeframe, chartLoadRequestIdRef.current);
  }, [indicatorVisibility.pivots, chartData, hasPivotLevelData, fetchBackgroundEnrichedChartData, selectedTimeframe]);

  /**
   * Apply / update Pivot S/R price lines (S1–S3, R1–R3) whenever
   * chart data or the pivots toggle changes.
   */
  useEffect(() => {
    // Validate chartData and pivot_levels exist with actual data
    const pivotLevels = chartData?.pivot_levels;
    const hasPivotData = pivotLevels && (
      pivotLevels.pivot || 
      (pivotLevels.support && pivotLevels.support.length > 0) || 
      (pivotLevels.resistance && pivotLevels.resistance.length > 0)
    );
    
    if (!chartData || !hasPivotData) {
      console.log('📊 Pivot levels not available in chart data:', pivotLevels);
      return;
    }
    if (!candlestickSeriesRef.current) {
      return;
    }

    // Remove existing pivot lines
    if (pivotLinesRef.current && pivotLinesRef.current.length > 0) {
      pivotLinesRef.current.forEach(line => {
        try {
          candlestickSeriesRef.current.removePriceLine(line);
        } catch (e) {
          console.error('Error removing pivot price line:', e);
        }
      });
      pivotLinesRef.current = [];
    }

    if (!indicatorVisibility.pivots) {
      return;
    }

    const { pivot, support = [], resistance = [] } = chartData.pivot_levels || {};
    console.log('📊 Drawing pivot levels:', { pivot, support, resistance });
    const newLines = [];

    const addLevel = (label, price, color) => {
      try {
        const line = candlestickSeriesRef.current.createPriceLine({
          price,
          color,
          lineWidth: 1,
          lineStyle: 2, // dashed
          axisLabelVisible: true,
          title: `${label} ${price.toFixed(2)}`,
        });
        newLines.push(line);
      } catch (e) {
        console.error('Error creating pivot price line:', e);
      }
    };

    // Pivot itself (optional, neutral color)
    if (pivot) {
      addLevel('P', Number(pivot), '#4b5563'); // gray
    }

    // Supports: S1–S3 (green)
    support.forEach(level => {
      if (level?.value) {
        addLevel(level.level || 'S', Number(level.value), '#22c55e');
      }
    });

    // Resistances: R1–R3 (red)
    resistance.forEach(level => {
      if (level?.value) {
        addLevel(level.level || 'R', Number(level.value), '#ef4444');
      }
    });

    pivotLinesRef.current = newLines;
    console.log(`✅ Drew ${newLines.length} pivot lines on chart`);
  }, [chartData, indicatorVisibility.pivots]);

  /**
   * Dynamic 1H Support/Resistance price lines rendering.
   *
   * Uses the /api/v1/nifty/sr-levels backend and a dedicated toggle.
   * Shown only on 15m / 30m / 60m NIFTY charts.
   */
  useEffect(() => {
    if (!candlestickSeriesRef.current) {
      return;
    }

    // Remove any existing dynamic SR lines first
    if (dynamicSrLinesRef.current && dynamicSrLinesRef.current.length > 0) {
      dynamicSrLinesRef.current.forEach(line => {
        try {
          candlestickSeriesRef.current.removePriceLine(line);
        } catch (e) {
          console.error('Error removing dynamic SR price line:', e);
        }
      });
      dynamicSrLinesRef.current = [];
    }

    const isSrTimeframe =
      selectedTimeframe === '5minute' ||
      selectedTimeframe === '15minute' ||
      selectedTimeframe === '30minute' ||
      selectedTimeframe === '60minute';

    if (!showDynamicSr || !dynamicSrLevels || !isSrTimeframe) {
      return;
    }

    const series = candlestickSeriesRef.current;
    const support = Array.isArray(dynamicSrLevels.support) ? dynamicSrLevels.support : [];
    const resistance = Array.isArray(dynamicSrLevels.resistance) ? dynamicSrLevels.resistance : [];

    const newLines = [];

    const addSrLine = (labelPrefix, lvl, defaultColor, flippedColor) => {
      const rawPrice = lvl?.price;
      if (rawPrice === undefined || rawPrice === null) {
        return;
      }
      const price = Number(rawPrice);
      if (!price || Number.isNaN(price)) {
        return;
      }
      const strength = typeof lvl.strength === 'number' ? lvl.strength : 0;
      const isFlipped = !!lvl.breakout;
      const color = isFlipped ? flippedColor : defaultColor;
      const lineWidth = strength >= 6 ? 2 : 1;
      const titleParts = [
        labelPrefix,
        price.toFixed(2),
        `S:${strength.toFixed(1)}`,
      ];
      if (isFlipped) {
        titleParts.push('Flip');
      }
      const title = titleParts.join(' | ');

      try {
        const line = series.createPriceLine({
          price,
          color,
          lineWidth,
          lineStyle: 2, // dashed
          axisLabelVisible: true,
          title,
        });
        newLines.push(line);
      } catch (e) {
        console.error('Error creating dynamic SR price line:', e);
      }
    };

    // Determine reference price (prefer livePrice, then latest chart candle)
    let refPrice = null;
    if (typeof livePrice === 'number' && !Number.isNaN(livePrice)) {
      refPrice = livePrice;
    } else if (chartData?.spot_price) {
      refPrice = chartData.spot_price;
    } else {
      const candles = historicalCandles.length > 0 ? historicalCandles : (chartData?.chart_data || []);
      if (candles.length > 0) {
        const lastCandle = candles[candles.length - 1];
        if (lastCandle && typeof lastCandle.close === 'number') {
          refPrice = lastCandle.close;
        }
      }
    }

    // Only show immediate levels: at most 2 nearest supports and 2 nearest resistances
    const pickNearest = (levels) => {
      if (!levels || levels.length === 0) return [];
      if (refPrice == null) {
        return levels.slice(0, 2);
      }
      return [...levels]
        .sort((a, b) => Math.abs((a.price || 0) - refPrice) - Math.abs((b.price || 0) - refPrice))
        .slice(0, 2);
    };

    const nearestSupport = pickNearest(support);
    const nearestResistance = pickNearest(resistance);

    // Draw strongest of the nearest levels first (sort by strength desc, then price)
    const strongSupport = [...nearestSupport].sort(
      (a, b) => (b.strength || 0) - (a.strength || 0) || (a.price || 0) - (b.price || 0)
    );
    const strongResistance = [...nearestResistance].sort(
      (a, b) => (b.strength || 0) - (a.strength || 0) || (a.price || 0) - (b.price || 0)
    );

    strongSupport.forEach(lvl => {
      // Default: green, flipped: blue
      addSrLine('S', lvl, '#10b981', '#3b82f6');
    });

    strongResistance.forEach(lvl => {
      // Default: red, flipped: purple
      addSrLine('R', lvl, '#ef4444', '#a855f7');
    });

    dynamicSrLinesRef.current = newLines;
    console.log(`✅ Drew ${newLines.length} dynamic SR lines on chart (nearest around ${refPrice ?? 'N/A'})`);
  }, [dynamicSrLevels, showDynamicSr, selectedTimeframe, livePrice, chartData, historicalCandles]);

  // FBRT (Failed Breakout Re-Test) price lines rendering - only on Daily timeframe
  useEffect(() => {
    if (!candlestickSeriesRef.current) {
      return;
    }

    // Remove existing FBRT lines
    if (fbrtLinesRef.current && fbrtLinesRef.current.length > 0) {
      fbrtLinesRef.current.forEach(line => {
        try {
          candlestickSeriesRef.current.removePriceLine(line);
        } catch (e) {
          console.error('Error removing FBRT price line:', e);
        }
      });
      fbrtLinesRef.current = [];
    }

    // Only show FBRT on daily timeframe
    if (!indicatorVisibility.fbrt || selectedTimeframe !== 'day') {
      return;
    }

    // Get FBRT annotations from chart data
    const fbrtAnnotations = chartData?.fbrt_annotations || [];
    
    if (!fbrtAnnotations || fbrtAnnotations.length === 0) {
      console.log('📊 FBRT: No patterns to display');
      return;
    }

    console.log('📊 Drawing FBRT patterns:', fbrtAnnotations);

    const newLines = [];

    // Draw price lines for each FBRT pattern
    fbrtAnnotations.forEach((pattern, patternIdx) => {
      const priceLines = pattern.price_lines || [];
      
      priceLines.forEach((lineConfig, lineIdx) => {
        try {
          const priceLine = candlestickSeriesRef.current.createPriceLine({
            price: lineConfig.price,
            color: lineConfig.color,
            lineWidth: lineConfig.lineWidth || 1,
            lineStyle: lineConfig.lineStyle || 0, // 0=Solid, 1=Dotted, 2=Dashed
            axisLabelVisible: lineConfig.axisLabelVisible !== false,
            title: lineConfig.title || '',
          });
          newLines.push(priceLine);
        } catch (e) {
          console.error(`Error creating FBRT price line ${patternIdx}-${lineIdx}:`, e);
        }
      });
    });

    fbrtLinesRef.current = newLines;
    console.log(`✅ Drew ${newLines.length} FBRT lines on chart for ${fbrtAnnotations.length} patterns`);
  }, [chartData, indicatorVisibility.fbrt, selectedTimeframe]);

  /**
   * Confluence Fib intraday overlays:
   * - TP1/TP2/TP3 horizontal price lines
   * - Risk zone band between entry and SL (shaded) when available
   */
  useEffect(() => {
    if (!candlestickSeriesRef.current || !chartRef.current) return;

    // Remove existing Fib lines
    if (fibTargetsLinesRef.current && fibTargetsLinesRef.current.length > 0) {
      fibTargetsLinesRef.current.forEach((line) => {
        try {
          candlestickSeriesRef.current.removePriceLine(line);
        } catch (e) {
          // Non-fatal: line might already be removed on chart recreation
        }
      });
      fibTargetsLinesRef.current = [];
    }

    // Remove existing Fib zone area series
    if (fibZoneAreaSeriesRef.current) {
      try {
        chartRef.current.removeSeries(fibZoneAreaSeriesRef.current);
      } catch (e) {
        // Ignore if chart was recreated
      }
      fibZoneAreaSeriesRef.current = null;
    }

    const sig = latestSignal;
    if (!sig || sig.strategy_type !== 'confluence_fib_intraday') return;

    const tp1 = sig.target_1;
    const tp2 = sig.target_2;
    const tp3 = sig.target_3;
    const entry = sig.entry_price;
    const stopLoss = sig.stop_loss;

    const addFibLine = (label, price, color) => {
      const p = Number(price);
      if (!Number.isFinite(p) || p <= 0) return;
      try {
        const line = candlestickSeriesRef.current.createPriceLine({
          price: p,
          color,
          lineWidth: 1,
          lineStyle: 2, // dashed
          axisLabelVisible: true,
          title: `${label} ${p.toFixed(2)}`,
        });
        fibTargetsLinesRef.current.push(line);
      } catch (e) {
        // Ignore failures for single line
      }
    };

    // Draw TP lines only when the numbers exist
    addFibLine('TP1', tp1, '#22c55e'); // green
    addFibLine('TP2', tp2, '#16a34a'); // darker green
    addFibLine('TP3', tp3, '#0ea5e9'); // blue

    // Risk zone shading between entry and SL (approx "buy/sell zone band")
    if (
      typeof entry === 'number' &&
      typeof stopLoss === 'number' &&
      Number.isFinite(entry) &&
      Number.isFinite(stopLoss) &&
      entry !== stopLoss &&
      chartData?.chart_data?.length
    ) {
      const bullish = sig.signal_type === 'Bullish';
      const lower = Math.min(entry, stopLoss);
      const upper = Math.max(entry, stopLoss);

      const fillTop = bullish ? 'rgba(34,197,94,0.18)' : 'rgba(239,68,68,0.18)';
      const fillBottom = bullish ? 'rgba(34,197,94,0.02)' : 'rgba(239,68,68,0.02)';
      const transparentLine = 'rgba(0,0,0,0)';

      try {
        const areaSeries = chartRef.current.addAreaSeries({
          topColor: fillTop,
          bottomColor: fillBottom,
          lineColor: transparentLine,
          lineWidth: 0,
          baseValue: { type: 'price', price: lower },
          priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
        });

        // Use a limited slice for performance; area series will visually extend.
        const candlesForBand = (chartData?.chart_data || []).slice(-120);
        const bandData = candlesForBand
          .map((c) => {
            const t = typeof c.time === 'number' ? c.time : Number(c.time);
            if (!Number.isFinite(t)) return null;
            return { time: t, value: upper };
          })
          .filter(Boolean);

        if (bandData.length > 0) {
          areaSeries.setData(bandData);
          fibZoneAreaSeriesRef.current = areaSeries;
        }
      } catch (e) {
        // Non-fatal: shading is optional
      }
    }
  }, [latestSignal, chartData, selectedTimeframe]);

  const renderChart = () => {
    // Show loading message while fetching chart data
    if (chartLoading) {
      return (
        <div className="mt-4 p-8 rounded bg-gray-50 border border-gray-200 text-center">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-700 font-medium">Chart is loading, please wait...</p>
            <p className="text-sm text-gray-500">Fetching historical data and indicators</p>
          </div>
        </div>
      );
    }
    
    // Show blocking chart error only when chart data is unavailable.
    // `error` is shared by analysis/websocket flows too, so avoid blanking an already-loaded chart.
    const hasChartRows = Boolean(chartData && chartData.chart_data && chartData.chart_data.length > 0);
    if (error && !hasChartRows) {
      const isBrokerError = error.toLowerCase().includes('zerodha') || 
                           error.toLowerCase().includes('broker') || 
                           error.toLowerCase().includes('connect');
      
      return (
        <div className="mt-4 p-4 rounded bg-red-50 border border-red-200 text-center">
          <p className="text-red-600 font-medium">Error loading chart data</p>
          <p className="text-sm text-red-500 mt-1">{error}</p>
          {isBrokerError && (
            <button
              onClick={() => navigate('/broker-integrations')}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Connect Broker Account →
            </button>
          )}
        </div>
      );
    }
    
    // Show error or no data message only after loading is complete
    if (!chartData || !chartData.chart_data || chartData.chart_data.length === 0) {
      return (
        <div className="mt-4 p-4 rounded bg-gray-50 border border-gray-200 text-center">
          <p className="text-gray-600">No chart data available</p>
          {chartData && !chartData.chart_data && (
            <p className="text-xs text-gray-500 mt-2">Data structure issue: chart_data is missing</p>
          )}
        </div>
      );
    }

    // Use live price from WebSocket if available, otherwise use chart data
    const latestPrice = livePrice || chartData.current_price || 0;
    const marketBias = chartData.market_bias || 'Neutral';
    const biasColor = marketBias === 'Bullish' ? 'text-green-600' : marketBias === 'Bearish' ? 'text-red-600' : 'text-gray-600';

    return (
      <div className="mt-4">
        {/* Chart Info */}
        <div className="p-4 rounded bg-white border border-gray-200 mb-4">
          <div className="flex flex-wrap justify-between items-center mb-3">
            <div>
              <h3 className="text-lg font-semibold">NIFTY 50 Chart ({chartData.timeframe})</h3>
          <div className="flex items-center gap-2">
                <p className="text-sm text-gray-600">Current Price: ₹{latestPrice.toFixed(2)}</p>
                {livePriceChange !== null && livePriceChange !== 0 && (
                  <span className={`text-xs font-semibold ${
                    livePriceChange >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {livePriceChange >= 0 ? '+' : ''}{livePriceChange.toFixed(2)}
                    {livePriceChangePercent !== null && (
                      <span className="ml-1">
                        ({livePriceChangePercent >= 0 ? '+' : ''}{livePriceChangePercent.toFixed(2)}%)
                      </span>
                    )}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {lastUpdate ? (
                  <>
                    Last updated: {lastUpdate.toLocaleTimeString()}
                    {wsConnected && (
                      <span className="ml-2 text-green-600">● Live</span>
                    )}
                  </>
                ) : (
                  <>
                    Current time: {currentTime.toLocaleTimeString()}
                    {wsConnected && (
                      <span className="ml-2 text-green-600">● Live</span>
                    )}
                  </>
                )}
              </p>
            </div>
            <div className="text-right">
              <p className={`text-sm font-semibold ${biasColor}`}>Market Bias: {marketBias}</p>
              <p className="text-xs text-gray-500">{chartData.data_points} data points</p>
              <div className="mt-2 flex items-center gap-2">
                <label className="flex items-center gap-1 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="w-3 h-3"
                  />
                  <span className="text-gray-600">Auto-refresh</span>
                </label>
                {autoRefresh && (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs flex items-center gap-1 ${
                      wsConnected 
                        ? 'text-green-600' 
                        : reconnectAttemptsRef.current > 0 
                          ? 'text-yellow-600' 
                          : 'text-red-600'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${
                        wsConnected 
                          ? 'bg-green-500 animate-pulse' 
                          : reconnectAttempts > 0
                            ? 'bg-yellow-500 animate-pulse'
                            : 'bg-red-500'
                      }`}></span>
                      {wsConnected 
                        ? 'Live' 
                        : reconnectAttempts > 0
                          ? `Reconnecting... (${reconnectAttempts})`
                          : 'Connecting...'}
                    </span>
                    {wsConnected && updateCount > 0 && (
                      <span className="text-xs text-gray-500">
                        ({updateCount} updates)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Legend with Toggle Buttons */}
          <div className="mb-2 flex flex-wrap gap-3 text-xs">
            <button
              onClick={() => {
                const newVisibility = !indicatorVisibility.ema9;
                setIndicatorVisibility(prev => ({ ...prev, ema9: newVisibility }));
                if (ema9SeriesRef.current) {
                  try {
                    if (typeof ema9SeriesRef.current.applyOptions === 'function') {
                      ema9SeriesRef.current.applyOptions({ visible: newVisibility });
                    } else if (typeof ema9SeriesRef.current.setVisible === 'function') {
                      ema9SeriesRef.current.setVisible(newVisibility);
                    }
                  } catch (error) {
                    console.error('Error toggling EMA 9 visibility:', error);
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                indicatorVisibility.ema9 
                  ? 'bg-blue-50 hover:bg-blue-100 border border-blue-300' 
                  : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={indicatorVisibility.ema9 ? 'Click to hide EMA 9' : 'Click to show EMA 9'}
            >
              <div className={`w-3 h-3 ${indicatorVisibility.ema9 ? 'bg-blue-500' : 'bg-gray-400'}`}></div>
              <span className={indicatorVisibility.ema9 ? 'text-gray-700' : 'text-gray-500'}>EMA 9</span>
            </button>
            <button
              onClick={() => {
                const newVisibility = !indicatorVisibility.ema20;
                setIndicatorVisibility(prev => ({ ...prev, ema20: newVisibility }));
                if (ema20SeriesRef.current) {
                  try {
                    if (typeof ema20SeriesRef.current.applyOptions === 'function') {
                      ema20SeriesRef.current.applyOptions({ visible: newVisibility });
                    } else if (typeof ema20SeriesRef.current.setVisible === 'function') {
                      ema20SeriesRef.current.setVisible(newVisibility);
                    }
                  } catch (error) {
                    console.error('Error toggling EMA 20 visibility:', error);
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                indicatorVisibility.ema20 
                  ? 'bg-fuchsia-50 hover:bg-fuchsia-100 border border-fuchsia-300' 
                  : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={indicatorVisibility.ema20 ? 'Click to hide EMA 20' : 'Click to show EMA 20'}
            >
              <div className={`w-3 h-3 ${indicatorVisibility.ema20 ? 'bg-fuchsia-500' : 'bg-gray-400'}`}></div>
              <span className={indicatorVisibility.ema20 ? 'text-gray-700' : 'text-gray-500'}>EMA 20</span>
            </button>
            <button
              onClick={() => {
                const newVisibility = !indicatorVisibility.maRibbon;
                setIndicatorVisibility(prev => ({ ...prev, maRibbon: newVisibility }));
                if (maRibbonSeriesRef.current) {
                  try {
                    if (typeof maRibbonSeriesRef.current.applyOptions === 'function') {
                      maRibbonSeriesRef.current.applyOptions({ visible: newVisibility });
                    } else if (typeof maRibbonSeriesRef.current.setVisible === 'function') {
                      maRibbonSeriesRef.current.setVisible(newVisibility);
                    }
                    if (newVisibility && chartData?.chart_data?.length) {
                      const ribbonData = buildMaRibbonDataFromCandles(
                        chartData.chart_data,
                        (c) => (typeof c.time === 'number' ? c.time : Number(c.time))
                      );
                      if (ribbonData.length > 0) {
                        maRibbonSeriesRef.current.setData(ribbonData);
                        const lastRibbon = ribbonData[ribbonData.length - 1];
                        maRibbonFastEmaRef.current = lastRibbon.fast;
                        maRibbonSlowEmaRef.current = lastRibbon.slow;
                      }
                    }
                  } catch (error) {
                    console.error('Error toggling MA Ribbon visibility:', error);
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                indicatorVisibility.maRibbon
                  ? 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-400'
                  : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={
                indicatorVisibility.maRibbon
                  ? 'Click to hide MA Ribbon (EMA 9/21)'
                  : 'Click to show MA Ribbon (EMA 9/21 crossover cloud)'
              }
            >
              <div
                className={`w-3 h-3 rounded-sm ${
                  indicatorVisibility.maRibbon
                    ? 'bg-gradient-to-br from-emerald-400 to-rose-400'
                    : 'bg-gray-400'
                }`}
              />
              <span className={indicatorVisibility.maRibbon ? 'text-gray-700' : 'text-gray-500'}>
                MA Ribbon
              </span>
            </button>
            <button
              onClick={() => {
                const newVisibility = !indicatorVisibility.pivots;
                setIndicatorVisibility(prev => ({ ...prev, pivots: newVisibility }));
                if (newVisibility && chartData?.chart_data?.length && !hasPivotLevelData(chartData)) {
                  void fetchBackgroundEnrichedChartData(selectedTimeframe, chartLoadRequestIdRef.current);
                }
                // When toggling off, remove existing pivot lines immediately
                if (!newVisibility && candlestickSeriesRef.current && pivotLinesRef.current?.length) {
                  try {
                    pivotLinesRef.current.forEach(line => {
                      try {
                        candlestickSeriesRef.current.removePriceLine(line);
                      } catch (e) {
                        console.error('Error removing pivot price line:', e);
                      }
                    });
                  } finally {
                    pivotLinesRef.current = [];
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                indicatorVisibility.pivots
                  ? 'bg-yellow-50 hover:bg-yellow-100 border border-yellow-300'
                  : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={indicatorVisibility.pivots ? 'Click to hide Pivot S/R levels' : 'Click to show Pivot S/R levels'}
            >
              <div className={`w-3 h-3 ${indicatorVisibility.pivots ? 'bg-yellow-500' : 'bg-gray-400'}`}></div>
              <span className={indicatorVisibility.pivots ? 'text-gray-700' : 'text-gray-500'}>Pivots (S1–S3, R1–R3)</span>
            </button>
            {/* Dynamic 1H Support/Resistance levels */}
            <button
              onClick={() => {
                const newValue = !showDynamicSr;
                setShowDynamicSr(newValue);
                // When toggling off, remove existing dynamic SR lines immediately
                if (!newValue && candlestickSeriesRef.current && dynamicSrLinesRef.current?.length) {
                  try {
                    dynamicSrLinesRef.current.forEach(line => {
                      try {
                        candlestickSeriesRef.current.removePriceLine(line);
                      } catch (e) {
                        console.error('Error removing dynamic SR price line:', e);
                      }
                    });
                  } finally {
                    dynamicSrLinesRef.current = [];
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                showDynamicSr
                  ? 'bg-blue-50 hover:bg-blue-100 border border-blue-300'
                  : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={showDynamicSr ? 'Click to hide Dynamic S/R levels' : 'Click to show Dynamic S/R levels (1H structure)'}
            >
              <div className={`w-3 h-3 ${showDynamicSr ? 'bg-blue-500' : 'bg-gray-400'}`}></div>
              <span className={showDynamicSr ? 'text-gray-700' : 'text-gray-500'}>Dynamic S/R (1H)</span>
            </button>
            {/* FBRT (Failed Breakout Re-Test) - Only available on Daily timeframe */}
            <button
              onClick={() => {
                if (selectedTimeframe !== 'day') {
                  // Show alert that FBRT only works on daily
                  alert('FBRT Analysis is only available on Daily timeframe. Please switch to Daily chart.');
                  return;
                }
                const newVisibility = !indicatorVisibility.fbrt;
                setIndicatorVisibility(prev => ({ ...prev, fbrt: newVisibility }));
                // When toggling off, remove existing FBRT lines immediately
                if (!newVisibility && candlestickSeriesRef.current && fbrtLinesRef.current?.length) {
                  try {
                    fbrtLinesRef.current.forEach(line => {
                      try {
                        candlestickSeriesRef.current.removePriceLine(line);
                      } catch (e) {
                        console.error('Error removing FBRT price line:', e);
                      }
                    });
                  } finally {
                    fbrtLinesRef.current = [];
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                selectedTimeframe !== 'day'
                  ? 'bg-gray-100 border border-gray-200 opacity-40 cursor-not-allowed'
                  : indicatorVisibility.fbrt
                    ? 'bg-cyan-50 hover:bg-cyan-100 border border-cyan-300'
                    : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={
                selectedTimeframe !== 'day'
                  ? 'FBRT Analysis only available on Daily timeframe'
                  : indicatorVisibility.fbrt
                    ? 'Click to hide FBRT Analysis'
                    : 'Click to show FBRT (Failed Breakout Re-Test) Analysis'
              }
            >
              <div className={`w-3 h-3 ${
                selectedTimeframe !== 'day'
                  ? 'bg-gray-300'
                  : indicatorVisibility.fbrt
                    ? 'bg-cyan-500'
                    : 'bg-gray-400'
              }`}></div>
              <span className={`${
                selectedTimeframe !== 'day'
                  ? 'text-gray-400'
                  : indicatorVisibility.fbrt
                    ? 'text-gray-700'
                    : 'text-gray-500'
              }`}>FBRT {selectedTimeframe !== 'day' ? '(Daily only)' : ''}</span>
            </button>
            <button
              onClick={() => {
                const newVisibility = !indicatorVisibility.ema34;
                setIndicatorVisibility(prev => ({ ...prev, ema34: newVisibility }));
                if (ema34SeriesRef.current) {
                  try {
                    if (typeof ema34SeriesRef.current.applyOptions === 'function') {
                      ema34SeriesRef.current.applyOptions({ visible: newVisibility });
                    } else if (typeof ema34SeriesRef.current.setVisible === 'function') {
                      ema34SeriesRef.current.setVisible(newVisibility);
                    }
                  } catch (error) {
                    console.error('Error toggling EMA 34 visibility:', error);
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                indicatorVisibility.ema34 
                  ? 'bg-orange-50 hover:bg-orange-100 border border-orange-300' 
                  : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={indicatorVisibility.ema34 ? 'Click to hide EMA 34' : 'Click to show EMA 34'}
            >
              <div className={`w-3 h-3 ${indicatorVisibility.ema34 ? 'bg-orange-500' : 'bg-gray-400'}`}></div>
              <span className={indicatorVisibility.ema34 ? 'text-gray-700' : 'text-gray-500'}>EMA 34</span>
            </button>
            <button
              onClick={() => {
                const newVisibility = !indicatorVisibility.vwap;
                setIndicatorVisibility(prev => ({ ...prev, vwap: newVisibility }));
                if (vwapSeriesRef.current) {
                  try {
                    if (typeof vwapSeriesRef.current.applyOptions === 'function') {
                      vwapSeriesRef.current.applyOptions({ visible: newVisibility });
                    } else if (typeof vwapSeriesRef.current.setVisible === 'function') {
                      vwapSeriesRef.current.setVisible(newVisibility);
                    }
                  } catch (error) {
                    console.error('Error toggling VWAP visibility:', error);
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                indicatorVisibility.vwap 
                  ? 'bg-purple-50 hover:bg-purple-100 border border-purple-300' 
                  : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={indicatorVisibility.vwap ? 'Click to hide VWAP' : 'Click to show VWAP'}
            >
              <div className={`w-3 h-3 border ${indicatorVisibility.vwap ? 'border-purple-500 border-dashed' : 'border-gray-400 border-dashed'}`}></div>
              <span className={indicatorVisibility.vwap ? 'text-gray-700' : 'text-gray-500'}>VWAP</span>
            </button>
            <button
              onClick={() => {
                const newVisibility = !indicatorVisibility.rsi14;
                setIndicatorVisibility(prev => ({ ...prev, rsi14: newVisibility }));
                if (rsiSeriesRef.current) {
                  try {
                    if (typeof rsiSeriesRef.current.applyOptions === 'function') {
                      rsiSeriesRef.current.applyOptions({ visible: newVisibility });
                    } else if (typeof rsiSeriesRef.current.setVisible === 'function') {
                      rsiSeriesRef.current.setVisible(newVisibility);
                    }
                  } catch (error) {
                    console.error('Error toggling RSI visibility:', error);
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                indicatorVisibility.rsi14 
                  ? 'bg-cyan-50 hover:bg-cyan-100 border border-cyan-300' 
                  : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={indicatorVisibility.rsi14 ? 'Click to hide RSI 14' : 'Click to show RSI 14'}
            >
              <div className={`w-3 h-3 ${indicatorVisibility.rsi14 ? 'bg-cyan-500' : 'bg-gray-400'}`}></div>
              <span className={indicatorVisibility.rsi14 ? 'text-gray-700' : 'text-gray-500'}>RSI 14</span>
            </button>
            <button
              onClick={() => {
                const newVisibility = !indicatorVisibility.supertrend1;
                setIndicatorVisibility(prev => ({ ...prev, supertrend1: newVisibility }));
                // Update visibility for all segments (now an array of series)
                if (supertrend1SeriesRef.current) {
                  try {
                    if (Array.isArray(supertrend1SeriesRef.current)) {
                      // Multiple series (segmented)
                      supertrend1SeriesRef.current.forEach(series => {
                        if (series && typeof series.applyOptions === 'function') {
                          series.applyOptions({ visible: newVisibility });
                        }
                      });
                    } else {
                      // Single series (old format - backward compatibility)
                      if (typeof supertrend1SeriesRef.current.applyOptions === 'function') {
                        supertrend1SeriesRef.current.applyOptions({ visible: newVisibility });
                      }
                    }
                  } catch (error) {
                    console.error('Error toggling SuperTrend 1 visibility:', error);
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                indicatorVisibility.supertrend1 
                  ? 'bg-green-50 hover:bg-green-100 border border-green-300' 
                  : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={indicatorVisibility.supertrend1 ? 'Click to hide SuperTrend (10, 1.5)' : 'Click to show SuperTrend (10, 1.5)'}
            >
              <div className={`w-3 h-3 ${indicatorVisibility.supertrend1 ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className={indicatorVisibility.supertrend1 ? 'text-gray-700' : 'text-gray-500'}>SuperTrend (10, 1.5)</span>
            </button>
            <button
              onClick={() => {
                const newVisibility = !indicatorVisibility.supertrend2;
                setIndicatorVisibility(prev => ({ ...prev, supertrend2: newVisibility }));
                // Update visibility for all segments (now an array of series)
                if (supertrend2SeriesRef.current) {
                  try {
                    if (Array.isArray(supertrend2SeriesRef.current)) {
                      // Multiple series (segmented)
                      supertrend2SeriesRef.current.forEach(series => {
                        if (series && typeof series.applyOptions === 'function') {
                          series.applyOptions({ visible: newVisibility });
                        }
                      });
                    } else {
                      // Single series (old format - backward compatibility)
                      if (typeof supertrend2SeriesRef.current.applyOptions === 'function') {
                        supertrend2SeriesRef.current.applyOptions({ visible: newVisibility });
                      }
                    }
                  } catch (error) {
                    console.error('Error toggling SuperTrend 2 visibility:', error);
                  }
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                indicatorVisibility.supertrend2 
                  ? 'bg-green-50 hover:bg-green-100 border border-green-300' 
                  : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 opacity-50'
              }`}
              title={indicatorVisibility.supertrend2 ? 'Click to hide SuperTrend (10, 2.0)' : 'Click to show SuperTrend (10, 2.0)'}
            >
              <div className={`w-3 h-3 ${indicatorVisibility.supertrend2 ? 'bg-[#4CAF50]' : 'bg-gray-400'}`}></div>
              <span className={indicatorVisibility.supertrend2 ? 'text-gray-700' : 'text-gray-500'}>SuperTrend (10, 2.0)</span>
            </button>
          </div>

          {/* Candlestick Chart */}
          <div className="mt-4 rounded border border-gray-200 overflow-hidden">
            <div ref={chartContainerRef} className="w-full" style={{ height: '500px' }} />
          </div>

          {/* Timeframe Selector */}
          <div className="mt-4 flex flex-wrap gap-2">
            {timeframes.map((tf) => (
              <button
                key={tf.value}
                onClick={() => setSelectedTimeframe(tf.value)}
                className={`px-3 py-1 text-sm rounded ${
                  selectedTimeframe === tf.value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>

          {/* Current Signal Info */}
          {chartData.current_signal && (
            <div className="mt-4 p-3 rounded bg-indigo-50 border border-indigo-200">
              <h4 className="font-semibold text-indigo-900">Current Signal</h4>
              <p className="text-sm text-indigo-700">
                {chartData.current_signal.signal_type} - {chartData.current_signal.instrument}
              </p>
              <p className="text-xs text-indigo-600 mt-1">
                Entry: ₹{Number(chartData.current_signal.entry_price).toFixed(2)}
              </p>
            </div>
          )}
        </div>

        {/* Patterns Section */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h4 className="text-sm font-semibold text-gray-600">Chart Overlays</h4>
            </div>
            {autoRefresh && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Live Detection
              </span>
            )}
          </div>

          {/* Pattern Filters - Always show for all timeframes */}
          <div className="mb-4 p-4 rounded border border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-semibold text-sm">Filter Patterns</h5>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showPatterns}
                    onChange={(e) => setShowPatterns(e.target.checked)}
                    className="w-3 h-3 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    disabled={!chartData.patterns || chartData.patterns.length === 0}
                  />
                  <span className={`${!chartData.patterns || chartData.patterns.length === 0 ? 'text-gray-400' : 'text-gray-700'}`}>Show on Chart</span>
                </label>
                <button
                  onClick={() => setPatternFilters({
                    patternType: 'all',
                    status: 'all',
                    signal: 'all',
                    startDate: '',
                    endDate: '',
                  })}
                  className="text-xs text-indigo-600 hover:text-indigo-800"
                >
                  Clear Filters
                </button>
              </div>
            </div>
            
            {chartData.patterns && chartData.patterns.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  {/* Pattern Type Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pattern Type
                    </label>
                    <select
                      value={patternFilters.patternType}
                      onChange={(e) => setPatternFilters(prev => ({ ...prev, patternType: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="all">All Patterns</option>
                      {Array.from(new Set(chartData.patterns.map(p => p.pattern_name))).map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={patternFilters.status}
                      onChange={(e) => setPatternFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="all">All Status</option>
                      <option value="Forming">Forming</option>
                      <option value="Confirmed">Confirmed</option>
                    </select>
                  </div>

                  {/* Signal Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Signal
                    </label>
                    <select
                      value={patternFilters.signal}
                      onChange={(e) => setPatternFilters(prev => ({ ...prev, signal: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="all">All Signals</option>
                      <option value="Bullish">Bullish</option>
                      <option value="Bearish">Bearish</option>
                    </select>
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={patternFilters.startDate}
                      onChange={(e) => setPatternFilters(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={patternFilters.endDate}
                      onChange={(e) => setPatternFilters(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-500 italic">
                No patterns detected for this timeframe. Try a different timeframe or wait for new patterns to form.
              </div>
            )}
          </div>

          {/* We no longer render the full pattern cards list here.
              Patterns are visualized directly on the chart via annotations,
              and users can refine what they see using the filters above. */}
          <div className="p-4 rounded border border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Use the filters above and the "Show on Chart" toggle to view pattern
              levels directly on the candlestick chart.
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      {!isMobileEmbed && <Navbar />}

      <div className={`flex-1 w-full px-4 ${isMobileEmbed ? 'py-3' : 'py-8'}`}>
        {!isMobileEmbed && (
          <div className="mb-6">
            <h1 className="text-3xl font-bold">NIFTY 50 AI Signal Analyzer</h1>
            <p className="text-gray-600 mt-1">Real-time chart analysis with pattern detection and trade signals.</p>
          </div>
        )}

        {/* Chart Section */}
        {renderChart()}

        {/* Auto Trade — opens dedicated page (same controls as mobile app) */}
        <div className="mt-6 p-4 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base font-extrabold text-emerald-800">Auto Trade</h2>
              <p className="text-xs sm:text-sm font-semibold text-emerald-900/80 mt-0.5 max-w-xl">
                Open the dedicated screen to configure lots, target, stop-loss, and activate automation on the sphere
                button (9:15 AM – 3:30 PM IST).
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/auto-trade')}
              className="shrink-0 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-sm transition-colors"
            >
              Open Auto Trade
            </button>
          </div>
        </div>

        {/* Latest Signal Section */}
        {latestSignal && (
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-800">Latest Signal</h2>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  latestSignal.signal_type === 'BUY_CE' || latestSignal.signal_type === 'HEDGE' ? 'bg-green-100 text-green-700' :
                  latestSignal.signal_type === 'BUY_PE' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {latestSignal.signal_type}
                </span>
              </div>
              {signalTimestamp && (
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Generated:</span>{' '}
                  {new Date(signalTimestamp).toLocaleString('en-IN', {
                    timeZone: 'Asia/Kolkata',
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })} IST
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <div>
                <div className="text-xs text-gray-600 mb-1">Strategy</div>
                <div className="font-semibold text-gray-800">{latestSignal.strategy_name || 'N/A'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Instrument</div>
                <div className="font-semibold text-gray-800">{latestSignal.instrument || 'N/A'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Entry Price</div>
                <div className="font-semibold text-gray-800">
                  {latestSignal.entry_price ? `₹${latestSignal.entry_price.toFixed(2)}` : 'N/A'}
                </div>
              </div>
            </div>
            {(latestSignal.stop_loss || latestSignal.target_1) && (() => {
              const latestMaRibbon = isMaRibbonSignal(latestSignal);
              const latestLevels = latestMaRibbon ? getMaRibbonTradeLevels(latestSignal) : null;
              const slFmt = latestMaRibbon
                ? formatMaRibbonPriceWithIndex(latestLevels?.stopLoss ?? latestSignal.stop_loss, latestLevels?.indexStopLoss)
                : null;
              const t1Fmt = latestMaRibbon
                ? formatMaRibbonPriceWithIndex(latestLevels?.target1 ?? latestSignal.target_1, latestLevels?.indexTarget1)
                : null;
              const showT2 = latestMaRibbon ? latestLevels?.target2 : latestSignal.target_2;
              const showT3 = latestMaRibbon ? latestLevels?.target3 : latestSignal.target_3;
              return (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <div className="flex flex-wrap gap-4 text-sm">
                  {(latestLevels?.stopLoss ?? latestSignal.stop_loss) != null && (
                  <div>
                    <span className="text-gray-600">Stop Loss:</span>{' '}
                    {latestMaRibbon && slFmt ? (
                      <span className="font-semibold text-red-600">
                        {slFmt.primary}
                        {slFmt.indexLine ? (
                          <span className="block text-xs font-normal text-gray-500">{slFmt.indexLine}</span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="font-semibold text-red-600">₹{latestSignal.stop_loss.toFixed(2)}</span>
                    )}
                  </div>
                  )}
                  {(latestLevels?.target1 ?? latestSignal.target_1) != null && (
                    <div>
                      <span className="text-gray-600">Target 1:</span>{' '}
                      {latestMaRibbon && t1Fmt ? (
                        <span className="font-semibold text-green-600">
                          {t1Fmt.primary}
                          {t1Fmt.indexLine ? (
                            <span className="block text-xs font-normal text-gray-500">{t1Fmt.indexLine}</span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="font-semibold text-green-600">₹{latestSignal.target_1.toFixed(2)}</span>
                      )}
                    </div>
                  )}
                  {latestMaRibbon && showT2 != null && (
                    <div>
                      <span className="text-gray-600">Target 2:</span>{' '}
                      <span className="font-semibold text-green-700">₹{Number(showT2).toFixed(2)}</span>
                    </div>
                  )}
                  {latestMaRibbon && showT3 != null && (
                    <div>
                      <span className="text-gray-600">Target 3:</span>{' '}
                      <span className="font-semibold text-blue-600">₹{Number(showT3).toFixed(2)}</span>
                    </div>
                  )}
                  {!latestMaRibbon && latestSignal.strategy_type === 'confluence_fib_intraday' && latestSignal.target_2 && (
                    <div>
                      <span className="text-gray-600">Target 2:</span>{' '}
                      <span className="font-semibold text-green-700">₹{latestSignal.target_2.toFixed(2)}</span>
                    </div>
                  )}
                  {!latestMaRibbon && latestSignal.strategy_type === 'confluence_fib_intraday' && latestSignal.target_3 && (
                    <div>
                      <span className="text-gray-600">Target 3:</span>{' '}
                      <span className="font-semibold text-blue-600">₹{latestSignal.target_3.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                {latestSignal.strategy_type === 'confluence_fib_intraday' && latestSignal.score_breakdown && (
                  <div className="mt-2 text-xs text-gray-700">
                    Scores:{' '}
                    Long{' '}
                    {typeof latestSignal.score_breakdown?.long_score === 'number'
                      ? `${latestSignal.score_breakdown.long_score}/6`
                      : 'N/A'}{' '}
                    | Short{' '}
                    {typeof latestSignal.score_breakdown?.short_score === 'number'
                      ? `${latestSignal.score_breakdown.short_score}/6`
                      : 'N/A'}{' '}
                    | Trigger{' '}
                    {typeof latestSignal.score_breakdown?.trigger_score === 'number' ? latestSignal.score_breakdown.trigger_score : 'N/A'}
                  </div>
                )}
              </div>
              );
            })()}
          </div>
        )}

        {/* Analysis Section */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Signal Analysis</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={refreshSignalStatus}
                disabled={loading}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Refresh status
              </button>
              {loading && (
                <span className="flex items-center gap-1 text-sm text-blue-600">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Refreshing...
                </span>
              )}
              {!loading && result && result.status === 'active_signal' && (() => {
                const signal = result.signal || result;
                const timestamp = signal?.timestamp || result?.timestamp;
                return timestamp ? (
                  <div className="text-xs text-gray-600">
                    <span className="font-medium">Generated:</span>{' '}
                    {new Date(timestamp).toLocaleString('en-IN', {
                      timeZone: 'Asia/Kolkata',
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })} IST
                  </div>
                ) : lastAnalysisTime ? (
                  <span className="text-xs text-gray-500">
                    Last: {lastAnalysisTime.toLocaleTimeString()}
                  </span>
                ) : null;
              })()}
              {!loading && (!result || result.status !== 'active_signal') && lastAnalysisTime && (
                <span className="text-xs text-gray-500">
                  Last: {lastAnalysisTime.toLocaleTimeString()}
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                analysisStatus === 'active_signal' ? 'bg-green-100 text-green-700' :
                analysisStatus === 'no_active_signal' ? 'bg-yellow-100 text-yellow-700' :
                analysisStatus === 'data_error' ? 'bg-red-100 text-red-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {analysisStatus === 'active_signal' ? '✓ Signal Active' :
                 analysisStatus === 'no_active_signal' ? '○ No Signal' :
                 analysisStatus === 'data_error' ? '✗ Data Error' :
                 'Scheduler (~5m)'}
              </span>
            </div>
          </div>
          {result?.opening_drive_override?.active && (
            <div
              className={`mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${
                String(result?.opening_drive_override?.direction || result?.opening_drive_override?.label || '').toLowerCase() === 'bearish'
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-green-50 border-green-200 text-green-700'
              }`}
            >
              <span>
                {String(result?.opening_drive_override?.direction || result?.opening_drive_override?.label || '').toLowerCase() === 'bearish'
                  ? 'Opening-drive bearish override active'
                  : 'Opening-drive bullish override active'}
              </span>
              {typeof result?.opening_drive_override?.gap_pct === 'number' && (
                <span className="opacity-80">Gap {Number(result.opening_drive_override.gap_pct).toFixed(2)}%</span>
              )}
            </div>
          )}

          <ClosingMomentumWindowPanel
            data={closingMomentumWindow}
            loading={closingMomentumLoading}
            error={closingMomentumError}
            onRefresh={fetchClosingMomentumWindow}
          />

          {(() => {
            const armed = result?.strategy_progress?.find((s) => {
              const name = String(s?.strategy_name || '').toLowerCase();
              return s?.status === 'ARMED' && name.includes('squeeze') && name.includes('breakout');
            });
            if (!armed) return null;
            const isBullish = String(armed?.signal_type || '').toLowerCase() === 'bullish';
            const text = isBullish
              ? '⚡⚡⚡ BREAKOUT SETUP DETECTED - BULLISH SQUEEZE ARMED!'
              : '⚡⚡⚡ BREAKOUT SETUP DETECTED - BEARISH SQUEEZE ARMED!';

            return (
              <div
                className={`mb-4 p-3 rounded-lg border text-center font-semibold ${
                  isBullish
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                {text}
              </div>
            );
          })()}

          {(() => {
            const hint = result?.ema7_15m_setup_hint;
            if (!hint?.show_banner) return null;
            if (result?.status === 'active_signal') return null;
            const isBullish = String(hint.signal_side || '').toLowerCase() === 'bullish';
            const isBearish = String(hint.signal_side || '').toLowerCase() === 'bearish';
            const style = isBullish
              ? 'bg-green-50 border-green-200 text-green-800'
              : isBearish
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-blue-50 border-blue-200 text-blue-800';
            const title = isBullish
              ? '🧲 EMA7 (15m) NEAR TOUCH - BULLISH SETUP POSSIBLE'
              : isBearish
                ? '🧲 EMA7 (15m) NEAR TOUCH - BEARISH SETUP POSSIBLE'
                : '🧲 EMA7 (15m) NEAR TOUCH - SETUP POSSIBLE';

            return (
              <div className={`mb-4 p-3 rounded-lg border text-center font-semibold ${style}`}>
                <div>{title}</div>
                <div className="mt-1 text-xs font-normal">
                  15m Close: ₹{Number(hint.close_15m || 0).toFixed(2)} | EMA7: ₹{Number(hint.ema7_15m || 0).toFixed(2)} | Dist: {Number(hint.distance_points || 0).toFixed(2)} pts
                </div>
              </div>
            );
          })()}

          {(() => {
            const gapSummary = result?.gap_regime_summary;
            if (!gapSummary?.is_gap_day) return null;
            const gapType = gapSummary.gap_type || 'unknown';
            const strategyMode = gapSummary.strategy_mode || 'normal';
            const gapDirection = gapSummary.gap_direction || 'none';
            const gapPct = typeof gapSummary.gap_pct === 'number' ? gapSummary.gap_pct : null;
            const orbUp = typeof gapSummary.orb_breakout_up === 'number' ? gapSummary.orb_breakout_up : null;
            const orbDown = typeof gapSummary.orb_breakout_down === 'number' ? gapSummary.orb_breakout_down : null;
            const message = result?.gap_filter_message || 'Gap-day mode active.';
            return (
              <div className="mb-4 p-3 rounded-lg border bg-blue-50 border-blue-200 text-blue-900">
                <div className="font-semibold">Gap-Day Mode</div>
                <div className="text-sm mt-1">{message}</div>
                <div className="text-xs mt-2 opacity-90">
                  Type: {gapType} | Mode: {strategyMode} | Direction: {gapDirection}
                  {gapPct !== null ? ` | Gap: ${gapPct.toFixed(2)}%` : ''}
                </div>
                {(orbUp !== null && orbDown !== null) ? (
                  <div className="text-xs opacity-90">ORB: Above {orbUp.toFixed(2)} / Below {orbDown.toFixed(2)}</div>
                ) : null}
              </div>
            );
          })()}

          {(() => {
            const msg = String(result?.message || '').trim().toLowerCase();
            const showNoTradeBanner =
              (result?.status === 'no_active_signal' || result?.status === 'no_signal') &&
              msg.includes('no-trade window');
            if (!showNoTradeBanner) return null;
            return (
              <div className="mb-4 p-3 rounded-lg border text-center font-semibold bg-amber-50 border-amber-200 text-amber-800">
                No-trade window (3:15–3:30 PM IST): signal generation paused.
              </div>
            );
          })()}

          {error && (
            <div className="mt-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-center">
              <p>{error}</p>
              {(error.toLowerCase().includes('zerodha') || 
                error.toLowerCase().includes('broker') || 
                error.toLowerCase().includes('connect')) && (
                <button
                  onClick={() => navigate('/broker-integrations')}
                  className="mt-3 px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Connect Broker Account →
                </button>
              )}
            </div>
          )}

          {cooldownNotice && (
            <div className="mt-3 p-2 rounded border border-amber-200 bg-amber-50 text-amber-800 text-sm">
              <span className="font-semibold">Blocked by cooldown/restriction:</span>{' '}
              {cooldownNotice.signalType}
              {cooldownNotice.instrument ? ` (${cooldownNotice.instrument})` : ''}{' '}
              from {cooldownNotice.strategy}. No new executable signal card shown.
            </div>
          )}

          {renderResult()}
        </div>

        {/* AURA Robot Widget */}
        <AuraWidget lastSignalData={result} />
      </div>

      {!isMobileEmbed && <Footer />}
    </div>
  );
};

export default NiftySignalAnalyzerPage;



